import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Language, CompleteKundali } from '../types/kundali';
import { formatDate, formatTime, formatSmartDateTime } from '../utils/dateUtils';
import { useClickOutside } from '../hooks/useClickOutside';
import { 
  Calendar, 
  Sparkles, 
  Search, 
  MapPin, 
  Clock, 
  CheckCircle2, 
  AlertTriangle, 
  Info, 
  ChevronRight, 
  ChevronDown,
  Filter, 
  X, 
  Globe, 
  ArrowRight,
  ArrowLeft,
  Navigation,
  Activity,
  ShieldCheck,
  Crosshair,
  Plus,
  Trash2,
  PlusCircle,
  Wand2,
  Edit2,
  Compass,
  Copy,
  Check,
  MessageSquare,
  BookOpen,
  ExternalLink,
  HelpCircle,
  Award,
  TrendingUp,
  ChevronUp,
  RotateCcw,
  Building2,
  Shield,
  Scale,
  Megaphone
} from 'lucide-react';
import { 
  findBestMuhurtas, 
  getPanchangaForTime, 
  calculateTaraBala, 
  calculateMuhurtaScore,
  DEFAULT_SHUDDHI_OPTIONS,
  SHUDDHI_PRESETS,
  getProfileStorageId,
  getProfileScopedStorageKey,
  compareMuhurtas,
  compareFutureMuhurtas
} from '../utils/muhurtaEngine';
import { MuhurtaAnalysis, VedicShuddhiOptions, NavataraFilterType, TaraType } from '../types/muhurta';
import { VedicShuddhiDropdown } from './VedicShuddhiDropdown';
import { NavataraFilterDropdown } from './NavataraFilterDropdown';
import { SwastikLogo } from './SwastikLogo';
import { MUHURTA_TAXONOMY, BUSINESS_TYPES, findMilestoneRules } from '../utils/muhurtaTaxonomy';
import { generateMuhurtaRationale } from '../utils/muhurtaRationaleEngine';
import { auth } from '../lib/firebase';
import { getUserSavedKundalis } from '../lib/kundaliService';
import { onAuthStateChanged } from 'firebase/auth';
import { NAKSHATRAS, ZODIAC_SIGNS } from '../utils/vedicEngine';

interface MuhurtaAdvisorProps {
  kundali: CompleteKundali;
  lang?: Language;
}

interface GeocodingResult {
  display_name: string;
  name: string;
  latitude: number;
  longitude: number;
  country: string;
  timezoneOffsetMinutes: number;
}

type Step = 'gathering' | 'results';

export const MuhurtaAdvisor: React.FC<MuhurtaAdvisorProps> = ({ kundali, lang = 'en' }) => {
  const isHi = lang === 'hi';
  const janmaNakIdx = kundali.planets.find(p => p.name === 'Moon')?.nakshatraIndex ?? 0;
  const janmaNakName = kundali.planets.find(p => p.name === 'Moon')?.nakshatra ?? 'Ashwini';

  const janmaRashiIdx = kundali.planets.find(p => p.name === 'Moon')?.signIndex ?? 0;
  const janmaRashiName = kundali.planets.find(p => p.name === 'Moon')?.sign ?? 'Aries';

  const [currentStep, setCurrentStep] = useState<Step>('gathering');
  const [mode, setMode] = useState<'general' | 'personalized'>('personalized');
  
  // Taxonomy states (User-driven progressive discovery)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  const [selectedMilestone, setSelectedMilestone] = useState<string | null>(null);
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>('manufacturing');

  // AI discovery states
  const [freeformDescription, setFreeformDescription] = useState('');
  const [isAnalyzingDescription, setIsAnalyzingDescription] = useState(false);
  const [aiClassificationResult, setAiClassificationResult] = useState<{
    detectedActivity: string;
    businessType: string;
    specificActivity: string;
    categoryId: string;
    milestoneId: string;
    confidence: string;
    explanation: string;
  } | null>(null);

  // Joint Muhurta states (including nakshatra and rashi indices)
  const [selectedPartners, setSelectedPartners] = useState<{ name: string; idx: number; rashiIdx?: number; source: string }[]>(() => [
    { name: kundali.birthDetails.name, idx: janmaNakIdx, rashiIdx: janmaRashiIdx, source: 'active' }
  ]);
  const [savedKundalis, setSavedKundalis] = useState<any[]>([]);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [showAddCustomPartner, setShowAddCustomPartner] = useState(false);
  const [customPartnerName, setCustomPartnerName] = useState('');
  const [customPartnerNakIdx, setCustomPartnerNakIdx] = useState<number>(0);
  const [customPartnerRashiIdx, setCustomPartnerRashiIdx] = useState<number>(0);

  // General parameters
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date(Date.now() + 7 * 86400000).toISOString().split('T')[0]);
  const [preferredDate, setPreferredDate] = useState<string>('');
  const [timePreference, setTimePreference] = useState<'any' | 'morning' | 'afternoon' | 'evening'>('any');
  const [primaryFocus, setPrimaryFocus] = useState<'combo' | 'nakshatra' | 'rashi'>('combo');
  const profileStorageId = useMemo(() => getProfileStorageId(kundali), [kundali]);
  const shuddhiStorageKey = useMemo(() => getProfileScopedStorageKey(profileStorageId, 'muhurta', 'shuddhi_options'), [profileStorageId]);
  const taraFilterStorageKey = useMemo(() => getProfileScopedStorageKey(profileStorageId, 'muhurta', 'tara_filter'), [profileStorageId]);

  // Non-disruptive UI notifications (replacing window.alert per Requirement 14)
  const [notification, setNotification] = useState<{ message: string; type: 'info' | 'warning' | 'error' } | null>(null);
  const showNotification = useCallback((message: string, type: 'info' | 'warning' | 'error' = 'warning') => {
    setNotification({ message, type });
    const timer = setTimeout(() => {
      setNotification(prev => prev?.message === message ? null : prev);
    }, 4500);
    return () => clearTimeout(timer);
  }, []);

  const [shuddhiOptions, setShuddhiOptions] = useState<VedicShuddhiOptions>(() => {
    try {
      const pid = getProfileStorageId(kundali);
      const key = getProfileScopedStorageKey(pid, 'muhurta', 'shuddhi_options');
      const saved = localStorage.getItem(key);
      if (saved) return JSON.parse(saved);
    } catch {}
    return DEFAULT_SHUDDHI_OPTIONS;
  });
  const [isUserCustomizedShuddhi, setIsUserCustomizedShuddhi] = useState(false);

  // Navatara Filter state - persists per profile (Requirement 13)
  const [selectedTaraFilter, setSelectedTaraFilter] = useState<NavataraFilterType>(() => {
    try {
      const pid = getProfileStorageId(kundali);
      const key = getProfileScopedStorageKey(pid, 'muhurta', 'tara_filter');
      const saved = localStorage.getItem(key);
      if (saved) return saved as NavataraFilterType;
    } catch {}
    return 'all_auspicious';
  });

  // Synchronize profile-scoped preferences when switching profiles (Requirement 13)
  useEffect(() => {
    try {
      const savedShuddhi = localStorage.getItem(shuddhiStorageKey);
      setShuddhiOptions(savedShuddhi ? JSON.parse(savedShuddhi) : DEFAULT_SHUDDHI_OPTIONS);
      setIsUserCustomizedShuddhi(Boolean(savedShuddhi));

      const savedTara = localStorage.getItem(taraFilterStorageKey);
      setSelectedTaraFilter(savedTara ? (savedTara as NavataraFilterType) : 'all_auspicious');
    } catch {}
  }, [profileStorageId, shuddhiStorageKey, taraFilterStorageKey]);

  const [customTaraTypes, setCustomTaraTypes] = useState<TaraType[]>([]);
  const [isUpdatingResults, setIsUpdatingResults] = useState(false);

  // Location states
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeocodingResult[]>([]);
  const [isSearchingLocation, setIsSearchingLocation] = useState(false);
  const [locations, setLocations] = useState<GeocodingResult[]>([]);
  const suggestionsRef = React.useRef<HTMLDivElement>(null);

  // Global Outside-Click & Escape Dismissal for Search Suggestions (V5.15-R7)
  useClickOutside(suggestionsRef, suggestions.length > 0, () => setSuggestions([]));

  const [resultsByLocation, setResultsByLocation] = useState<{[key: string]: MuhurtaAnalysis[]}>({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [detailedViewId, setDetailedViewId] = useState<string | null>(null);
  const [copiedSummaryIdx, setCopiedSummaryIdx] = useState<number | null>(null);
  const abortControllerRef = React.useRef<AbortController | null>(null);

  // 3-Month Future Horizon Discovery State
  const [betterFutureMuhurtas, setBetterFutureMuhurtas] = useState<{
    muhurta: MuhurtaAnalysis;
    locationName: string;
    scoreDifference: number;
  }[]>([]);
  const [isCheckingFuture, setIsCheckingFuture] = useState(false);
  const [expandedFutureIds, setExpandedFutureIds] = useState<Set<string>>(new Set());
  // Single shared sort preference governing every Muhurta result collection
  const [sortOrder, setSortOrder] = useState<'date' | 'score'>('date');
  const [visibleFutureCount, setVisibleFutureCount] = useState(6);
  const [showScoreExplanationModal, setShowScoreExplanationModal] = useState(false);
  // Shastric Action Guidance section collapse state (collapsed by default)
  const [expandedGuidanceKeys, setExpandedGuidanceKeys] = useState<Set<string>>(new Set());

  const toggleFutureExpanded = (id: string) => {
    setExpandedFutureIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGuidance = (key: string) => {
    setExpandedGuidanceKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Original requested dates state to allow going back at any time
  const [originalSearchDates, setOriginalSearchDates] = useState<{
    startDate: string;
    endDate: string;
    preferredDate?: string;
  } | null>(null);

  const isDatesModifiedFromOriginal = Boolean(
    originalSearchDates && (
      startDate !== originalSearchDates.startDate ||
      endDate !== originalSearchDates.endDate ||
      (preferredDate || '') !== (originalSearchDates.preferredDate || '')
    )
  );

  // Sync selected active partner when the main Kundali profile changes
  useEffect(() => {
    // When the user selects or switches to a profile, default strictly to this single profile
    setSelectedPartners([
      { name: kundali.birthDetails.name, idx: janmaNakIdx, rashiIdx: janmaRashiIdx, source: 'active' }
    ]);
    // Clear any previous profile's calculated results to guarantee freshness for the newly selected profile
    setResultsByLocation({});
    setBetterFutureMuhurtas([]);
    setDetailedViewId(null);
    setCurrentStep('gathering');
  }, [kundali.birthDetails.name, janmaNakIdx, janmaRashiIdx]);

  // Jump to top of different time window when changing insights / expanding a window
  useEffect(() => {
    if (detailedViewId !== null) {
      const targetId = `muhurta-window-${detailedViewId}`;
      const timer = setTimeout(() => {
        const el = document.getElementById(targetId);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 60);
      return () => clearTimeout(timer);
    }
  }, [detailedViewId]);

  // Fetch saved Kundalis for joint calculation
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setLoadingSaved(true);
        try {
          const records = await getUserSavedKundalis(user.uid);
          const filtered = records.filter(rec => rec.kundaliData?.birthDetails?.name !== kundali.birthDetails.name);
          setSavedKundalis(filtered);
        } catch (e) {
          console.error('Error loading saved charts:', e);
        } finally {
          setLoadingSaved(false);
        }
      } else {
        setSavedKundalis([]);
      }
    });
    return () => unsubscribe();
  }, [kundali.birthDetails.name]);

  const activeCategory = selectedCategory ? MUHURTA_TAXONOMY.find(c => c.id === selectedCategory) : null;
  const activeSubCategory = (selectedCategory && selectedSubCategory)
    ? activeCategory?.subCategories?.find(s => s.id === selectedSubCategory)
    : null;
  const activeMilestone = selectedMilestone ? findMilestoneRules(selectedMilestone) : null;

  const getActivityQuestion = () => {
    switch (selectedCategory) {
      case 'property': return isHi ? 'संपत्ति/निर्माण का स्थान' : 'Property/Construction Location';
      case 'business': return isHi ? 'कार्यालय/व्यापार का स्थान' : 'Office/Business Location';
      case 'travel': return isHi ? 'प्रस्थान का स्थान' : 'Departure Location';
      default: return isHi ? 'गतिविधि का स्थान' : 'Activity Location';
    }
  };

  // Debounced search for location
  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3 || currentStep !== 'gathering') {
      setSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      setIsSearchingLocation(true);
      try {
        const res = await fetch(`/api/geocoding?q=${encodeURIComponent(searchQuery)}`);
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data);
        }
      } catch (e) {
        console.error('Geocoding search failed:', e);
      } finally {
        setIsSearchingLocation(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchQuery, currentStep]);

  const stopCalculation = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setIsCalculating(false);
    }
  };

  const restartSearch = () => {
    stopCalculation();
    setResultsByLocation({});
    setBetterFutureMuhurtas([]);
    setIsCheckingFuture(false);
    setExpandedFutureIds(new Set());
    setOriginalSearchDates(null);
    setCurrentStep('gathering');
    setDetailedViewId(null);
    setAiClassificationResult(null);
    setFreeformDescription('');
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedMilestone(null);
  };

  const handleSearch = async (
    overrideFocus?: 'combo' | 'nakshatra' | 'rashi',
    overrideStartDate?: string,
    overrideEndDate?: string,
    overrideShuddhi?: VedicShuddhiOptions,
    overrideTaraFilter?: NavataraFilterType,
    overrideCustomTaras?: TaraType[]
  ) => {
    if (!selectedMilestone) {
      showNotification(isHi ? 'कृपया पहले गतिविधि का चयन करें।' : 'Please select an activity / milestone first.', 'warning');
      return;
    }
    if (locations.length === 0) {
      showNotification(isHi ? 'कृपया मुहूर्त गणना के लिए कम से कम एक स्थान चुनें।' : 'Please select at least one location for Muhurta calculation.', 'warning');
      return;
    }
    const targetFocus = overrideFocus || primaryFocus;
    const effectiveStart = overrideStartDate || preferredDate || startDate;
    const effectiveEnd = overrideEndDate || preferredDate || endDate;
    const activeShuddhi = overrideShuddhi || shuddhiOptions;
    const activeTaraFilter = overrideTaraFilter !== undefined ? overrideTaraFilter : selectedTaraFilter;
    const activeCustomTaras = overrideCustomTaras !== undefined ? overrideCustomTaras : customTaraTypes;

    let targetTaraTypes: TaraType[] | undefined = undefined;
    if (activeTaraFilter === 'kshema_only') targetTaraTypes = ['Kshema'];
    else if (activeTaraFilter === 'sadhaka_only') targetTaraTypes = ['Sadhaka'];
    else if (activeTaraFilter === 'sampat_only') targetTaraTypes = ['Sampat'];
    else if (activeTaraFilter === 'mitra_only') targetTaraTypes = ['Mitra', 'Param Mitra'];
    else if (activeTaraFilter === 'custom') targetTaraTypes = activeCustomTaras;

    // Save as original search dates when initiated directly by the user (not an override switch)
    if (!overrideStartDate && !overrideEndDate) {
      setOriginalSearchDates({
        startDate,
        endDate,
        preferredDate
      });
    }

    // In-place updating avoids destroying the view / jarring full-page reload
    const isUpdatingInPlace = currentStep === 'results' && Object.keys(resultsByLocation).length > 0;
    if (isUpdatingInPlace) {
      setIsUpdatingResults(true);
    } else {
      setIsCalculating(true);
      setCurrentStep('results');
      setResultsByLocation({});
    }

    setBetterFutureMuhurtas([]);
    setExpandedFutureIds(new Set());
    
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Determine preferred time window
    let preferredTimeRange = undefined;
    if (timePreference === 'morning') preferredTimeRange = { start: '06:00', end: '11:59' };
    else if (timePreference === 'afternoon') preferredTimeRange = { start: '12:00', end: '16:59' };
    else if (timePreference === 'evening') preferredTimeRange = { start: '17:00', end: '21:00' };

    const newResults: {[key: string]: MuhurtaAnalysis[]} = {};

    try {
      for (const loc of locations) {
        if (signal.aborted) break;

        const searchResults = await findBestMuhurtas(
          {
            activityId: selectedMilestone,
            latitude: loc.latitude,
            longitude: loc.longitude,
            startDate: effectiveStart,
            endDate: effectiveEnd,
            preferredTimeRange,
            janmaNakIdxs: mode === 'personalized' ? selectedPartners.map(p => ({ name: p.name, idx: p.idx })) : undefined,
            janmaRashiIdxs: mode === 'personalized' ? selectedPartners.map(p => ({ name: p.name, idx: p.rashiIdx ?? 0 })) : undefined,
            framework: targetFocus,
            customRules: activeMilestone?.rules,
            shuddhiOptions: activeShuddhi,
            navataraFilter: activeTaraFilter,
            targetTaraTypes: targetTaraTypes,
            sortBy: sortOrder
          },
          undefined,
          signal
        );

        if (!signal.aborted) {
          newResults[loc.display_name] = searchResults;
          setResultsByLocation(prev => ({ ...prev, [loc.display_name]: searchResults }));
        }
      }

      if (!signal.aborted) {
        // Keep detailed view collapsed by default in the result page as requested
        setDetailedViewId(null);

        // Compute current best score across all locations
        const allCurrent = Object.values(newResults).flat();
        const currentBest = allCurrent.length > 0
          ? Math.max(...allCurrent.map(r => r.score?.totalScore ?? 0))
          : 0;

        // Perform 3-month scan in background to discover all higher-scoring Muhurtas matching same filters
        scanNext3Months(locations[0], targetFocus, currentBest, preferredTimeRange, signal, allCurrent, activeShuddhi, activeTaraFilter, targetTaraTypes);
      }
    } catch (e) {
      console.error('Calculation error:', e);
    } finally {
      if (!signal.aborted) {
        setIsCalculating(false);
        setIsUpdatingResults(false);
      }
    }
  };

  // Scan next 3 months (90 days) for all higher scoring Muhurta windows
  const scanNext3Months = async (
    loc: GeocodingResult,
    targetFocus: 'combo' | 'nakshatra' | 'rashi',
    currentBestScore: number,
    preferredTimeRange: { start: string; end: string } | undefined,
    signal: AbortSignal,
    currentResults: MuhurtaAnalysis[],
    activeShuddhi?: VedicShuddhiOptions,
    activeTaraFilter?: NavataraFilterType,
    activeTargetTaras?: TaraType[]
  ) => {
    if (!selectedMilestone || !loc) return;
    setIsCheckingFuture(true);
    setBetterFutureMuhurtas([]);
    setExpandedFutureIds(new Set());

    try {
      const today = new Date();
      const futureStart = today.toISOString().split('T')[0];
      const futureEnd = new Date(today.getTime() + 90 * 86400000);
      const futureEndStr = futureEnd.toISOString().split('T')[0];

      const futureResults = await findBestMuhurtas(
        {
          activityId: selectedMilestone,
          latitude: loc.latitude,
          longitude: loc.longitude,
          startDate: futureStart,
          endDate: futureEndStr,
          preferredTimeRange,
          janmaNakIdxs: mode === 'personalized' ? selectedPartners.map(p => ({ name: p.name, idx: p.idx })) : undefined,
          janmaRashiIdxs: mode === 'personalized' ? selectedPartners.map(p => ({ name: p.name, idx: p.rashiIdx ?? 0 })) : undefined,
          framework: targetFocus,
          customRules: activeMilestone?.rules,
          shuddhiOptions: activeShuddhi || shuddhiOptions,
          navataraFilter: activeTaraFilter || selectedTaraFilter,
          targetTaraTypes: activeTargetTaras,
          maxTransits: 120,
          sortBy: sortOrder,
          limit: 100
        },
        undefined,
        signal
      );

      if (signal.aborted) return;

      if (futureResults && futureResults.length > 0) {
        // Collect dates already represented in the current search results
        const existingDates = new Set(
          currentResults.map(r => new Date(r.dateTime).toISOString().split('T')[0])
        );

        // Group by distinct date so we pick the best slot on each superior date
        const dateMap = new Map<string, MuhurtaAnalysis>();
        for (const cand of futureResults) {
          const candDate = new Date(cand.dateTime).toISOString().split('T')[0];
          if (existingDates.has(candDate)) continue;
          const isScoreBetter = currentBestScore > 0 
            ? cand.score.totalScore > currentBestScore 
            : cand.score.totalScore >= 70;
          if (!isScoreBetter) continue;

          const prev = dateMap.get(candDate);
          if (!prev || cand.score.totalScore > prev.score.totalScore) {
            dateMap.set(candDate, cand);
          }
        }

        const superiorList = Array.from(dateMap.values())
          .map(topWindow => ({
            muhurta: topWindow,
            locationName: loc.name || loc.display_name,
            scoreDifference: currentBestScore > 0 ? (topWindow.score.totalScore - currentBestScore) : 0
          }))
          .sort((a, b) => b.muhurta.score.totalScore - a.muhurta.score.totalScore);

        setBetterFutureMuhurtas(superiorList);
      } else {
        setBetterFutureMuhurtas([]);
      }
    } catch (e) {
      console.warn('3-month future scan failed or aborted:', e);
      if (!signal.aborted) {
        setBetterFutureMuhurtas([]);
      }
    } finally {
      if (!signal.aborted) {
        setIsCheckingFuture(false);
      }
    }
  };

  // Adopt the 3-month future Muhurta date immediately
  const handleAdoptFutureMuhurta = (m: MuhurtaAnalysis) => {
    // Save original search dates if not already recorded
    if (!originalSearchDates) {
      setOriginalSearchDates({
        startDate,
        endDate,
        preferredDate
      });
    }

    const targetDate = new Date(m.dateTime).toISOString().split('T')[0];
    setStartDate(targetDate);
    setEndDate(targetDate);
    setPreferredDate(targetDate);
    handleSearch(primaryFocus, targetDate, targetDate);
    setTimeout(() => {
      const el = document.getElementById('results-list');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Revert back to the user's original requested date
  const handleRevertToOriginalDate = () => {
    if (!originalSearchDates) return;
    const origStart = originalSearchDates.startDate;
    const origEnd = originalSearchDates.endDate;
    const origPref = originalSearchDates.preferredDate || '';
    setStartDate(origStart);
    setEndDate(origEnd);
    setPreferredDate(origPref);
    handleSearch(primaryFocus, origStart, origEnd);
    setTimeout(() => {
      const el = document.getElementById('results-list');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDetectLocation = () => {
    if (!navigator.geolocation) {
      showNotification(isHi ? "ब्राउज़र में जियोलोकेशन समर्थित नहीं है।" : "Geolocation is not supported by your browser.", "error");
      return;
    }
    
    setIsSearchingLocation(true);
    navigator.geolocation.getCurrentPosition(async (position) => {
      const { latitude, longitude } = position.coords;
      try {
        const res = await fetch(`/api/reverse-geocoding?lat=${latitude}&lon=${longitude}`);
        if (res.ok) {
           const data = await res.json();
           if (!locations.find(l => l.display_name === data.display_name)) {
             setLocations(prev => [...prev, data]);
           }
        } else {
           setLocations(prev => [...prev, {
             display_name: `Lat: ${latitude.toFixed(2)}, Lon: ${longitude.toFixed(2)}`,
             name: 'Current Location',
             latitude,
             longitude,
             country: '',
             timezoneOffsetMinutes: 0
           }]);
        }
      } catch (e) {
        console.error('Reverse geocoding failed', e);
        setLocations(prev => [...prev, {
          display_name: `Lat: ${latitude.toFixed(2)}, Lon: ${longitude.toFixed(2)}`,
          name: 'Current Location',
          latitude,
          longitude,
          country: '',
          timezoneOffsetMinutes: 0
        }]);
      } finally {
        setIsSearchingLocation(false);
      }
    }, (error) => {
      console.error(error);
      setIsSearchingLocation(false);
      showNotification(isHi ? "स्थान का पता नहीं लगाया जा सका।" : "Could not detect location.", "error");
    });
  };

  // AI classification triggering
  const handleAIClassify = async () => {
    if (!freeformDescription || freeformDescription.trim().length < 5) {
      showNotification(isHi ? 'कृपया कम से कम 5 वर्णों का वर्णनात्मक विवरण दर्ज करें।' : 'Please enter a descriptive activity of at least 5 characters.', 'warning');
      return;
    }
    setIsAnalyzingDescription(true);
    try {
      const res = await fetch('/api/muhurta/classify-activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: freeformDescription })
      });
      if (res.ok) {
        const data = await res.json();
        setAiClassificationResult(data);
        // Automatically map taxonomy states based on AI response
        if (data.categoryId) {
          setSelectedCategory(data.categoryId);
          const cat = MUHURTA_TAXONOMY.find(c => c.id === data.categoryId);
          if (cat?.subCategories && data.milestoneId) {
            const foundSub = cat.subCategories.find(s => s.milestones?.some(m => m.id === data.milestoneId));
            if (foundSub) {
              setSelectedSubCategory(foundSub.id);
            } else {
              setSelectedSubCategory(cat.subCategories[0]?.id || null);
            }
          } else {
            setSelectedSubCategory(null);
          }
        }
        if (data.milestoneId) {
          setSelectedMilestone(data.milestoneId);
        }
      } else {
        showNotification(isHi ? 'गतिविधि का वर्गीकरण नहीं हो सका। मैन्युअल चयन पर जा रहे हैं।' : 'Could not classify activity. Falling back to manual selection.', 'info');
      }
    } catch (e) {
      console.error('AI Activity discovery failed:', e);
      showNotification(isHi ? 'वर्गीकरण के दौरान त्रुटि हुई। कृपया मैन्युअल रूप से चुनें।' : 'An error occurred during classification. Please select manually.', 'error');
    } finally {
      setIsAnalyzingDescription(false);
    }
  };

  // User-driven progressive category selection handlers
  const handleSelectCategory = (catId: string) => {
    if (selectedCategory === catId) {
      // Toggle / deselect if clicked again
      setSelectedCategory(null);
      setSelectedSubCategory(null);
      setSelectedMilestone(null);
      return;
    }
    setSelectedCategory(catId);
    const cat = MUHURTA_TAXONOMY.find(c => c.id === catId);
    if (cat?.subCategories && cat.subCategories.length === 1) {
      // If there is only ONE sub category (e.g. Digital & Technology), select that by default
      setSelectedSubCategory(cat.subCategories[0].id);
      setSelectedMilestone(null);
    } else {
      setSelectedSubCategory(null);
      setSelectedMilestone(null);
    }
  };

  const handleSelectSubCategory = (subId: string) => {
    if (selectedSubCategory === subId) {
      setSelectedSubCategory(null);
      setSelectedMilestone(null);
      return;
    }
    setSelectedSubCategory(subId);
    setSelectedMilestone(null);
  };

  const handleSelectMilestone = (milestoneId: string) => {
    setSelectedMilestone(milestoneId);
    // Classical auto-preset based on milestone nature (only if user hasn't manually customized):
    if (!isUserCustomizedShuddhi) {
      if (['register_company', 'start_business', 'open_current_account', 'purchase_property', 'invest_funds', 'launch_website', 'inauguration'].includes(milestoneId)) {
        setShuddhiOptions(SHUDDHI_PRESETS['lifelong_business'].options);
      } else if (['vehicle_delivery', 'dispatch_goods', 'travel'].includes(milestoneId)) {
        setShuddhiOptions(SHUDDHI_PRESETS['quick_trade'].options);
      }
    }
  };

  const handleResetTaxonomy = () => {
    setSelectedCategory(null);
    setSelectedSubCategory(null);
    setSelectedMilestone(null);
    setAiClassificationResult(null);
  };

  // Add guest partner
  const handleAddCustomPartner = () => {
    if (!customPartnerName.trim()) {
      showNotification(isHi ? 'कृपया नाम दर्ज करें।' : 'Please provide a partner name.', 'warning');
      return;
    }
    if (selectedPartners.length >= 4) {
      showNotification(isHi ? 'आप संयुक्त मुहूर्त गणना के लिए अधिकतम 4 व्यक्तियों को जोड़ सकते हैं।' : 'You can add a maximum of 4 individuals for joint Muhurta calculation.', 'warning');
      return;
    }
    setSelectedPartners(prev => [
      ...prev,
      { name: customPartnerName.trim(), idx: customPartnerNakIdx, rashiIdx: customPartnerRashiIdx, source: 'guest' }
    ]);
    setCustomPartnerName('');
    setShowAddCustomPartner(false);
  };

  // Toggle saved Kundali selection
  const handleToggleSavedPartner = (rec: any) => {
    const isSelected = selectedPartners.some(p => p.name === rec.name);
    if (isSelected) {
      setSelectedPartners(prev => prev.filter(p => p.name !== rec.name));
    } else {
      if (selectedPartners.length >= 4) {
        showNotification(isHi ? 'आप अधिकतम 4 व्यक्तियों का चयन कर सकते हैं।' : 'You can select up to 4 individuals.', 'warning');
        return;
      }
      const partnerNakIdx = rec.kundaliData?.planets?.find((p: any) => p.name === 'Moon')?.nakshatraIndex ?? 0;
      const partnerRashiIdx = rec.kundaliData?.planets?.find((p: any) => p.name === 'Moon')?.signIndex ?? 0;
      setSelectedPartners(prev => [
        ...prev,
        { name: rec.name, idx: partnerNakIdx, rashiIdx: partnerRashiIdx, source: 'saved' }
      ]);
    }
  };

  const getScoreColor = (label: string) => {
    switch (label) {
      case 'Excellent': return 'text-emerald-700 bg-emerald-50 border-emerald-200';
      case 'Very Good': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'Good': return 'text-cyan-700 bg-cyan-50 border-cyan-200';
      case 'Average': return 'text-[#52635F] bg-astro-secondary-surface border-[#E2DED5]';
      default: return 'text-rose-700 bg-rose-50 border-rose-200';
    }
  };

  const getTaraColor = (suitability: string) => {
    switch (suitability) {
      case 'Excellent': return 'text-emerald-700';
      case 'Good': return 'text-blue-700';
      case 'Danger': return 'text-rose-700';
      case 'Poor': return 'text-[#52635F]';
      default: return 'text-astro-secondary-text';
    }
  };

  return (
    <div className="space-y-6 max-w-5xl mx-auto" id="muhurta-advisor-root">
      {/* Header with Breadcrumbs */}
      <div className="bg-astro-bg border border-astro-border rounded-2xl p-5 shadow-sm" id="advisor-header">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-astro-border pb-5 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-astro-primary p-2 rounded-xl shadow-sm">
              <Calendar className="w-6 h-6 text-astro-accent" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-xl font-bold text-astro-primary font-serif">
                  {isHi ? 'वैयक्तिकृत मुहूर्त सलाहकार' : 'Comprehensive Muhurta Advisor'}
                </h2>
                {kundali?.birthDetails?.name && (
                  <span className="text-xs bg-astro-primary/10 text-astro-primary font-bold px-2.5 py-0.5 rounded-full border border-astro-primary/20">
                    👤 {kundali.birthDetails.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-astro-secondary-text">
                {isHi ? 'तारा बल और पंचक के साथ श्रेष्ठ और सटीक ज्योतिषीय गणना' : 'Activity-specific, multi-partner Vedic alignment engine'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-1">
            {([
              { id: 'gathering', label: isHi ? 'आवश्यकताएँ' : 'Requirements', num: 1 },
              { id: 'results', label: isHi ? 'परिणाम' : 'Results', num: 2 },
            ] as { id: Step; label: string; num: number }[]).map((stepItem) => {
              const isAccessible = stepItem.id === 'gathering' || 
                (stepItem.id === 'results' && resultsByLocation && Object.keys(resultsByLocation).length > 0);

              return (
                <React.Fragment key={stepItem.id}>
                  <button
                    type="button"
                    onClick={() => {
                      if (isAccessible) {
                        setCurrentStep(stepItem.id);
                      }
                    }}
                    disabled={!isAccessible}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer ${
                      currentStep === stepItem.id 
                        ? 'bg-astro-primary text-white shadow-xs' 
                        : isAccessible 
                        ? 'bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border' 
                        : 'bg-astro-card/50 text-astro-secondary-text/50 cursor-not-allowed border border-astro-border/50'
                    }`}
                  >
                    <span className={`w-4 h-4 rounded-full flex items-center justify-center text-[8px] ${currentStep === stepItem.id ? 'bg-white/20' : 'bg-astro-border'}`}>
                      {stepItem.num}
                    </span>
                    {stepItem.label}
                  </button>
                  {stepItem.num < 2 && <ChevronRight className="w-3 h-3 text-astro-border shrink-0" />}
                </React.Fragment>
              );
            })}
          </div>
        </div>

        {/* Wizard Steps */}
        <div className="min-h-[280px]" id="step-content">
          {currentStep === 'gathering' && (
            <div className="animate-in fade-in slide-in-from-right-4 duration-300 space-y-6" id="activity-step">
              
              {/* Personalized vs General Switch */}
              <div className="flex flex-wrap gap-2 sm:gap-3">
                <button
                  id="mode-personalized-btn"
                  onClick={() => setMode('personalized')}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${mode === 'personalized' ? 'bg-astro-primary text-white shadow-md' : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'}`}
                >
                  {isHi ? 'व्यक्तिगत / संयुक्त' : 'Personalized / Joint (with Kundali)'}
                </button>
                <button
                  id="mode-general-btn"
                  onClick={() => setMode('general')}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer ${mode === 'general' ? 'bg-astro-primary text-white shadow-md' : 'bg-white border border-astro-border text-astro-secondary-text hover:bg-astro-card'}`}
                >
                  {isHi ? 'सामान्य' : 'General Muhurta (No Kundali)'}
                </button>
              </div>

              {/* Personalized Area with multi-kundali selection */}
              {mode === 'personalized' && (
                    <div className="p-5 bg-[#F9F7F3] rounded-2xl border border-astro-border space-y-4" id="personalized-panel">
                      <div>
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                            {isHi ? 'मुहूर्त की गणना हेतु जातक (अधिकतम 4):' : 'Individuals Included (Max 4 for Joint Business/Property):'}
                          </h4>
                          {selectedPartners.length > 1 && (
                            <button
                              onClick={() => setSelectedPartners([{ name: kundali.birthDetails.name, idx: janmaNakIdx, rashiIdx: janmaRashiIdx, source: 'active' }])}
                              className="text-[10px] bg-astro-primary/10 hover:bg-astro-primary/20 text-astro-primary px-2.5 py-1 rounded-lg font-bold transition-all self-start sm:self-auto flex items-center gap-1"
                            >
                              <X className="w-3 h-3" />
                              {isHi ? 'केवल इस कुंडली के लिए' : 'Use This Profile Only'}
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selectedPartners.map((partner, index) => (
                            <div key={index} className="bg-white border border-astro-border px-3 py-1.5 rounded-xl flex items-center gap-2 shadow-sm text-xs">
                              <span className="font-bold text-astro-primary">{partner.name}</span>
                              <span className="text-[10px] bg-astro-primary/5 text-astro-accent px-1.5 py-0.5 rounded">
                                {NAKSHATRAS[partner.idx]?.name || 'Ashwini'}
                              </span>
                              {partner.rashiIdx !== undefined && (
                                <span className="text-[10px] bg-amber-500/10 text-amber-900 border border-amber-200/60 px-1.5 py-0.5 rounded">
                                  {ZODIAC_SIGNS[partner.rashiIdx]} Rashi
                                </span>
                              )}
                              {index > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedPartners(prev => prev.filter((_, i) => i !== index))}
                                  className="text-rose-500 hover:text-rose-700 cursor-pointer"
                                  aria-label={isHi ? `${partner.name} को हटाएं` : `Remove ${partner.name}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Saved & Custom Partner Adders */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-astro-border/50">
                        {/* Saved Charts Selection */}
                        <div>
                          <h5 className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider mb-2">
                            {isHi ? 'सुरक्षित कुंडलियों में से चुनें:' : 'Select from Saved Kundalis:'}
                          </h5>
                          {loadingSaved ? (
                            <div className="text-xs text-astro-secondary-text italic">Loading saved profiles...</div>
                          ) : savedKundalis.length === 0 ? (
                            <div className="text-xs text-astro-secondary-text opacity-60">No other saved Kundalis found.</div>
                          ) : (
                            <div className="max-h-28 overflow-y-auto space-y-1 pr-1 border border-astro-border/50 rounded-lg p-2 bg-white">
                              {savedKundalis.map(rec => {
                                const isChecked = selectedPartners.some(p => p.name === rec.name);
                                return (
                                  <label key={rec.id} className="flex items-center gap-2 text-xs text-astro-secondary-text hover:bg-[#F9F7F3] p-1 rounded cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => handleToggleSavedPartner(rec)}
                                      className="rounded text-astro-primary focus:ring-astro-primary"
                                    />
                                    <span className="font-semibold">{rec.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Add Guest Partner Form */}
                        <div>
                          <h5 className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider mb-2">
                            {isHi ? 'नया साथी जोड़ें (अतिथि):' : 'Add Guest Partner:'}
                          </h5>
                          {showAddCustomPartner ? (
                            <div className="space-y-2 bg-white p-3 rounded-xl border border-astro-border">
                              <input
                                type="text"
                                placeholder="Partner Name"
                                value={customPartnerName}
                                onChange={(e) => setCustomPartnerName(e.target.value)}
                                className="w-full text-xs p-2 border border-astro-border rounded-lg outline-none"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <label className="text-[10px] text-astro-secondary-text font-semibold block mb-0.5">Nakshatra</label>
                                  <select
                                    value={customPartnerNakIdx}
                                    onChange={(e) => setCustomPartnerNakIdx(Number(e.target.value))}
                                    className="w-full text-xs p-1.5 border border-astro-border rounded-lg bg-white"
                                  >
                                    {NAKSHATRAS.map((nak, i) => (
                                      <option key={i} value={i}>{nak.name}</option>
                                    ))}
                                  </select>
                                </div>
                                <div>
                                  <label className="text-[10px] text-astro-secondary-text font-semibold block mb-0.5">Moon Rashi</label>
                                  <select
                                    value={customPartnerRashiIdx}
                                    onChange={(e) => setCustomPartnerRashiIdx(Number(e.target.value))}
                                    className="w-full text-xs p-1.5 border border-astro-border rounded-lg bg-white"
                                  >
                                    {ZODIAC_SIGNS.map((sign, i) => (
                                      <option key={i} value={i}>{sign}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={handleAddCustomPartner}
                                  className="bg-astro-primary text-white px-3 py-1 rounded text-[10px] font-bold cursor-pointer"
                                >
                                  Add
                                </button>
                                <button
                                  onClick={() => setShowAddCustomPartner(false)}
                                  className="border border-astro-border text-astro-secondary-text px-3 py-1 rounded text-[10px] cursor-pointer"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              onClick={() => setShowAddCustomPartner(true)}
                              disabled={selectedPartners.length >= 4}
                              className="flex items-center gap-1 text-xs font-bold text-astro-primary hover:text-astro-secondary cursor-pointer"
                            >
                              <PlusCircle className="w-4 h-4" />
                              {isHi ? 'अतिथि विवरण दर्ज करें' : 'Add guest partner name, Nakshatra & Rashi'}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Active Selection Pathway Breadcrumb Header */}
                  {selectedCategory && (
                    <div className="flex items-center justify-between bg-white border border-astro-border rounded-xl p-3 shadow-xs animate-in fade-in duration-200">
                      <div className="flex items-center gap-2 flex-wrap text-xs">
                        <span className="font-bold text-astro-secondary-text uppercase text-[10px] tracking-wider">
                          {isHi ? 'चयनित मार्ग:' : 'Selection Pathway:'}
                        </span>
                        <span className="font-bold text-astro-primary bg-astro-card px-2.5 py-1 rounded-lg border border-astro-border">
                          {isHi && activeCategory?.hiLabel ? activeCategory.hiLabel : activeCategory?.label}
                        </span>
                        {selectedSubCategory && activeSubCategory && (
                          <>
                            <ChevronRight className="w-3.5 h-3.5 text-astro-secondary-text shrink-0" />
                            <span className="font-bold text-astro-primary bg-astro-card px-2.5 py-1 rounded-lg border border-astro-border">
                              {isHi && activeSubCategory.hiLabel ? activeSubCategory.hiLabel : activeSubCategory.label}
                            </span>
                          </>
                        )}
                        {selectedMilestone && activeMilestone && (
                          <>
                            <ChevronRight className="w-3.5 h-3.5 text-astro-secondary-text shrink-0" />
                            <span className="font-bold text-white bg-astro-primary px-2.5 py-1 rounded-lg shadow-xs flex items-center gap-1">
                              <Check className="w-3 h-3 text-astro-accent" />
                              {isHi && activeMilestone.hiLabel ? activeMilestone.hiLabel : activeMilestone.label}
                            </span>
                          </>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={handleResetTaxonomy}
                        className="text-[11px] font-bold text-rose-600 hover:text-rose-800 hover:bg-rose-50 px-2 py-1 rounded-lg transition-colors flex items-center gap-1 cursor-pointer shrink-0"
                        title={isHi ? 'श्रेणी रीसेट करें' : 'Reset Selection'}
                      >
                        <X className="w-3.5 h-3.5" />
                        {isHi ? 'रीसेट' : 'Reset'}
                      </button>
                    </div>
                  )}

                  {/* Step 1: Major Category Selector */}
                  <div className="space-y-2.5">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold text-astro-primary uppercase tracking-widest flex items-center gap-2">
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                          selectedCategory ? 'bg-astro-primary text-white' : 'bg-astro-primary/10 text-astro-primary'
                        }`}>
                          1
                        </span>
                        {isHi ? '१. प्रमुख श्रेणी चुनें' : '1. Select Major Category'}
                      </h3>
                      {selectedCategory && (
                        <span className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          {isHi ? 'श्रेणी चयनित' : 'Category Selected'}
                        </span>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-2 sm:gap-2.5" id="category-pills">
                      {MUHURTA_TAXONOMY.map(cat => {
                        const isSelected = selectedCategory === cat.id;
                        return (
                          <button
                            key={cat.id}
                            type="button"
                            onClick={() => handleSelectCategory(cat.id)}
                            title={isHi && cat.hiLabel ? cat.hiLabel : cat.label}
                            className={`px-4 py-2.5 rounded-xl border text-xs font-semibold whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                              isSelected
                                ? 'bg-astro-primary border-astro-primary text-white shadow-xs font-bold ring-2 ring-astro-accent/40'
                                : 'bg-white border-astro-border text-astro-secondary-text hover:bg-[#F9F7F3] hover:text-astro-primary'
                            }`}
                          >
                            {isSelected && <Check className="w-3.5 h-3.5 text-astro-accent" />}
                            {isHi && cat.hiLabel ? cat.hiLabel : cat.label}
                          </button>
                        );
                      })}
                    </div>

                    {!selectedCategory && (
                      <p className="text-[11px] text-astro-secondary-text/80 italic pt-1">
                        {isHi 
                          ? '👆 अपनी गतिविधि की श्रेणी चुनने के लिए ऊपर दिए गए किसी भी विकल्प पर क्लिक करें।' 
                          : '👆 Click on any category above to view available sub-categories and specific auspicious milestones.'}
                      </p>
                    )}
                  </div>

                  {/* Step 2: SubCategory if exists for selected category */}
                  {selectedCategory && activeCategory?.subCategories && activeCategory.subCategories.length > 0 && (
                    <div className="space-y-2.5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold text-astro-primary uppercase tracking-widest flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            selectedSubCategory ? 'bg-astro-primary text-white' : 'bg-astro-primary/10 text-astro-primary'
                          }`}>
                            2
                          </span>
                          {isHi ? '२. उप-श्रेणी चुनें' : '2. Select Sub-Category'}
                        </h3>
                        {selectedSubCategory && (
                          <span className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {isHi ? 'उप-श्रेणी चयनित' : 'Sub-Category Selected'}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2" id="subcategory-flex">
                        {activeCategory.subCategories.map(sub => {
                          const isSelected = selectedSubCategory === sub.id;
                          return (
                            <button
                              key={sub.id}
                              type="button"
                              onClick={() => handleSelectSubCategory(sub.id)}
                              title={isHi && sub.hiLabel ? sub.hiLabel : sub.label}
                              className={`px-3.5 py-2 rounded-xl border text-xs whitespace-nowrap transition-all cursor-pointer flex items-center gap-1.5 ${
                                isSelected
                                  ? 'bg-astro-primary border-astro-primary text-white shadow-xs font-bold ring-2 ring-astro-accent/40'
                                  : 'bg-white border-astro-border text-astro-secondary-text hover:bg-[#F9F7F3] hover:text-astro-primary'
                              }`}
                            >
                              {isSelected && <Check className="w-3.5 h-3.5 text-astro-accent" />}
                              {isHi && sub.hiLabel ? sub.hiLabel : sub.label}
                            </button>
                          );
                        })}
                      </div>

                      {!selectedSubCategory && (
                        <p className="text-[11px] text-astro-secondary-text/80 italic pt-1">
                          {isHi 
                            ? '👆 विशिष्ट अनुष्ठान और कार्यों को देखने के लिए उप-श्रेणी चुनें।' 
                            : '👆 Choose a sub-category above to reveal specific rituals and milestones.'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Step 3: Milestones / Specific Actions */}
                  {selectedCategory && (!activeCategory?.subCategories || selectedSubCategory) && (
                    <div className="space-y-2.5 pt-2 animate-in fade-in slide-in-from-top-2 duration-300">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xs font-bold text-astro-primary uppercase tracking-widest flex items-center gap-2">
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            selectedMilestone ? 'bg-astro-primary text-white' : 'bg-astro-primary/10 text-astro-primary'
                          }`}>
                            {activeCategory?.subCategories ? '3' : '2'}
                          </span>
                          {isHi ? '३. विशिष्ट गतिविधि या अनुष्ठान चुनें' : '3. Select Specific Milestone / Action'}
                        </h3>
                        {selectedMilestone && (
                          <span className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {isHi ? 'मील का पत्थर चयनित' : 'Milestone Selected'}
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3" id="milestones-grid">
                        {(selectedSubCategory
                          ? activeCategory?.subCategories?.find(s => s.id === selectedSubCategory)?.milestones
                          : activeCategory?.milestones
                        )?.map(milestone => {
                          const isSelected = selectedMilestone === milestone.id;
                          return (
                            <button
                              key={milestone.id}
                              type="button"
                              onClick={() => handleSelectMilestone(milestone.id)}
                              title={isHi && milestone.hiLabel ? milestone.hiLabel : milestone.label}
                              className={`p-4 rounded-2xl border text-left transition-all flex flex-col justify-between min-h-[6.5rem] cursor-pointer ${
                                isSelected
                                  ? 'bg-astro-primary border-astro-primary text-white shadow-md ring-2 ring-astro-accent/40'
                                  : 'bg-white border-astro-border text-astro-secondary-text hover:bg-[#F9F7F3] hover:border-astro-primary/30'
                              }`}
                            >
                              <div>
                                <div className="flex items-start justify-between gap-1">
                                  <div className="text-xs font-bold leading-snug">
                                    {isHi && milestone.hiLabel ? milestone.hiLabel : milestone.label}
                                  </div>
                                  {isSelected && <Check className="w-4 h-4 text-astro-accent shrink-0 mt-0.5" />}
                                </div>
                                <div className={`text-[11px] line-clamp-2 mt-1.5 leading-relaxed ${isSelected ? 'text-white/85' : 'text-astro-secondary-text'}`}>
                                  {milestone.description}
                                </div>
                              </div>
                              {milestone.rules?.source && (
                                <div className={`text-[9px] mt-2 font-mono uppercase tracking-wider ${isSelected ? 'text-white/70' : 'text-astro-secondary-text/60'}`}>
                                  📖 {milestone.rules.source}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      {!selectedMilestone && (
                        <p className="text-[11px] text-astro-secondary-text/80 italic pt-1">
                          {isHi 
                            ? '👆 शुभ मुहूर्त गणना के लिए विशिष्ट कार्य कार्ड पर क्लिक करें।' 
                            : '👆 Click on a specific milestone card above to lock in the Vedic calculation profile.'}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Business Type Selector if Category is Business */}
                  {selectedCategory === 'business' && (
                    <div className="p-5 bg-white border border-astro-border rounded-2xl space-y-3 animate-in fade-in duration-300" id="business-types-panel">
                      <h4 className="text-xs font-bold text-astro-primary uppercase tracking-wider">
                        {isHi ? 'व्यवसाय का प्रकार चुनें (सटीक ज्योतिषीय नियमों के लिए):' : 'Select Business Type (For Specific Astrology Calibration):'}
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                        {BUSINESS_TYPES.map(bt => (
                          <button
                            key={bt.id}
                            type="button"
                            onClick={() => setSelectedBusinessType(bt.id)}
                            className={`p-2.5 rounded-lg border text-xs truncate text-center cursor-pointer ${
                              selectedBusinessType === bt.id
                                ? 'bg-astro-accent border-astro-accent text-white font-bold'
                                : 'bg-white border-astro-border text-astro-secondary-text hover:bg-[#F9F7F3]'
                            }`}
                          >
                            {isHi ? bt.hiLabel : bt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* AI-Assisted Activity Discovery */}
                  <div className="p-5 bg-[#FDFBF7] border border-dashed border-astro-accent rounded-2xl space-y-3" id="ai-classify-panel">
                    <div className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4 text-astro-accent" />
                      <h4 className="text-xs font-bold text-astro-accent uppercase tracking-widest">
                        {isHi ? 'क्या आपकी विशिष्ट गतिविधि सूची में नहीं है? एआई का उपयोग करें' : 'Can\'t find your exact task? Ask Vedic AI to Classify'}
                      </h4>
                    </div>
                    <p className="text-xs text-astro-secondary-text">
                      {isHi 
                        ? 'नीचे अपनी आगामी गतिविधि या व्यवसाय का वर्णन करें (उदा. "एल्युमिनियम खिड़की बनाने की नई फैक्ट्री खोलना"):' 
                        : 'Describe your upcoming action below (e.g. "I am opening a factory that manufactures aluminium windows"). Our AI will map it to the closest Vedic scriptural rule profile without fabricating mock rules.'}
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={freeformDescription}
                        onChange={(e) => setFreeformDescription(e.target.value)}
                        placeholder={isHi ? 'यहाँ लिखें...' : 'Describe what you are doing (e.g., launching a tech startup)...'}
                        className="flex-1 bg-white border border-astro-border px-4 py-2.5 rounded-xl text-xs font-bold outline-none text-astro-primary"
                      />
                      <button
                        onClick={handleAIClassify}
                        disabled={isAnalyzingDescription}
                        className="bg-astro-primary text-white px-5 py-2.5 rounded-xl font-bold text-xs flex items-center gap-2 hover:bg-astro-secondary transition-all disabled:opacity-50 cursor-pointer"
                      >
                        {isAnalyzingDescription ? <Clock className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 text-astro-accent" />}
                        {isHi ? 'एआई वर्गीकृत' : 'Classify'}
                      </button>
                    </div>

                    {/* AI Classification Success Banner */}
                    {aiClassificationResult && (
                      <div className="p-3 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl space-y-1 text-xs">
                        <div className="font-bold flex items-center gap-1.5 text-emerald-900">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          Activity Classified: {aiClassificationResult.detectedActivity} ({aiClassificationResult.confidence} Confidence)
                        </div>
                        <div className="text-[11px] opacity-95">
                          <strong>Specific Segment:</strong> {aiClassificationResult.businessType} &bull; <strong>Recommended Rule:</strong> {aiClassificationResult.explanation}
                        </div>
                      </div>
                    )}
                  </div>

              {/* Section 2: Location Selection */}
              <div className="pt-8 border-t border-astro-border/60 max-w-2xl mx-auto w-full" id="location-section">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-astro-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-astro-primary" />
                </div>
                <h3 className="text-xl font-bold text-astro-primary font-serif mb-2">{getActivityQuestion()}</h3>
                <p className="text-sm text-astro-secondary-text">
                  {isHi ? 'कृपया उस स्थान का पिन कोड दर्ज करें या शहर का नाम खोजें जहाँ कार्य निष्पादित होगा।' : 'Specify the location or enter the PIN code of where the action takes place.'}
                </p>
              </div>

              {/* Selected Locations List */}
              {locations.length > 0 && (
                <div className="space-y-2 mb-6" id="selected-locations-list">
                  {locations.map((loc, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white border border-astro-border p-3 rounded-xl shadow-sm">
                      <div className="flex items-center gap-3 overflow-hidden">
                        <div className="w-8 h-8 bg-astro-card rounded-lg flex items-center justify-center shrink-0">
                          <MapPin className="w-4 h-4 text-astro-accent" />
                        </div>
                        <div className="overflow-hidden">
                          <div className="text-sm font-bold text-astro-text truncate">{loc.name || loc.display_name}</div>
                          <div className="text-[10px] text-astro-secondary-text truncate">{loc.display_name}</div>
                        </div>
                      </div>
                      <button 
                        type="button"
                        onClick={() => setLocations(prev => prev.filter((_, i) => i !== idx))}
                        className="p-1.5 hover:bg-rose-50 text-astro-secondary-text hover:text-rose-500 rounded-lg transition-colors cursor-pointer"
                        title={isHi ? 'स्थान हटाएं' : 'Remove location'}
                        aria-label={isHi ? `${loc.name || loc.display_name} स्थान हटाएं` : `Remove location ${loc.name || loc.display_name}`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="mb-10 relative" ref={suggestionsRef}>
                <div className="flex gap-2 mb-2">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none">
                      {isSearchingLocation ? <Clock className="w-4 h-4 text-astro-secondary-text animate-spin" /> : <Search className="w-4 h-4 text-astro-secondary-text" />}
                    </div>
                    <input 
                      type="text"
                      id="location-search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder={isHi ? 'शहर या पिन कोड खोजें...' : 'Search city or PIN code...'}
                      className="w-full bg-white border border-astro-border focus:border-astro-primary rounded-2xl pl-11 pr-4 py-3 text-sm font-bold text-astro-primary outline-none transition-all shadow-sm"
                    />
                  </div>
                  <button
                    onClick={handleDetectLocation}
                    className="bg-astro-primary text-white p-3 rounded-2xl hover:bg-astro-primary/90 transition-all flex items-center justify-center shrink-0"
                    title={isHi ? 'मेरा स्थान खोजें' : 'Detect My Location'}
                  >
                    <Crosshair className="w-5 h-5" />
                  </button>
                </div>
                
                {suggestions.length > 0 && (
                  <div className="z-50 left-0 right-0 mt-1 bg-white border border-astro-border rounded-xl shadow-2xl overflow-hidden max-h-60 overflow-y-auto">
                    {suggestions.map((s, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          if (!locations.find(l => l.display_name === s.display_name)) {
                            setLocations(prev => [...prev, s]);
                          }
                          setSearchQuery('');
                          setSuggestions([]);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-astro-card border-b border-astro-border/50 last:border-0 transition-colors flex items-center gap-3"
                      >
                        <MapPin className="w-4 h-4 text-astro-accent shrink-0" />
                        <div>
                          <div className="text-sm font-bold text-astro-text">{s.name || s.display_name}</div>
                          <div className="text-[10px] text-astro-secondary-text">{s.display_name}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              </div>

              {/* Section 3: Date Range & Preferences */}
              <div className="pt-8 border-t border-astro-border/60 max-w-2xl mx-auto w-full" id="date-section">
              <div className="text-center mb-8">
                <h3 className="text-xl font-bold text-astro-primary font-serif mb-2">{isHi ? 'समय और वरीयता' : 'When do you plan to start?'}</h3>
                <div className="flex flex-wrap items-center justify-center gap-2 text-xs text-astro-secondary-text bg-astro-card px-4 py-2 rounded-full border border-astro-border/50 w-fit mx-auto">
                  <MapPin className="w-3.5 h-3.5" /> 
                  {locations.length === 0 
                    ? (isHi ? 'कोई स्थान चयनित नहीं' : 'No location selected')
                    : locations.length === 1 
                    ? locations[0].name || locations[0].display_name 
                    : `${locations.length} Locations Selected`}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">{isHi ? 'प्रारंभ तिथि' : 'Start Date'}</label>
                    <input 
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-white border border-astro-border rounded-xl px-4 py-2.5 text-sm font-bold text-astro-primary outline-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">{isHi ? 'अंतिम तिथि' : 'End Date'}</label>
                    <input 
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-white border border-astro-border rounded-xl px-4 py-2.5 text-sm font-bold text-astro-primary outline-none"
                    />
                  </div>

                    {/* Quick Date Range Presets */}
                    <div className="flex flex-wrap gap-1.5 pt-1">
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const tomorrow = new Date(today.getTime() + 86400000);
                          setStartDate(today.toISOString().split('T')[0]);
                          setEndDate(tomorrow.toISOString().split('T')[0]);
                        }}
                        className="text-[10px] bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer"
                      >
                        ⚡ {isHi ? 'अगले २ दिन' : 'Next 2 Days'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const nextWeek = new Date(today.getTime() + 7 * 86400000);
                          setStartDate(today.toISOString().split('T')[0]);
                          setEndDate(nextWeek.toISOString().split('T')[0]);
                        }}
                        className="text-[10px] bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer"
                      >
                        📅 {isHi ? '७ दिन' : '7 Days'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const nextFortnight = new Date(today.getTime() + 15 * 86400000);
                          setStartDate(today.toISOString().split('T')[0]);
                          setEndDate(nextFortnight.toISOString().split('T')[0]);
                        }}
                        className="text-[10px] bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer"
                      >
                        🌙 {isHi ? '१५ दिन' : '15 Days Window'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const today = new Date();
                          const nextMonth = new Date(today.getTime() + 30 * 86400000);
                          setStartDate(today.toISOString().split('T')[0]);
                          setEndDate(nextMonth.toISOString().split('T')[0]);
                        }}
                        className="text-[10px] bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer"
                      >
                        🗓️ {isHi ? '३० दिन (१ माह)' : '30 Days (1 Month)'}
                      </button>

                      {isDatesModifiedFromOriginal && originalSearchDates && (
                        <button
                          type="button"
                          onClick={() => {
                            setStartDate(originalSearchDates.startDate);
                            setEndDate(originalSearchDates.endDate);
                            setPreferredDate(originalSearchDates.preferredDate || '');
                          }}
                          className="text-[10px] bg-amber-100 hover:bg-amber-200 text-amber-950 border border-amber-300 px-2.5 py-1 rounded-lg font-bold transition-all cursor-pointer flex items-center gap-1 shadow-2xs"
                          title="Restore originally requested dates"
                        >
                          <RotateCcw className="w-3 h-3 text-amber-800" />
                          <span>{isHi ? 'मूल अनुरोधित तारीख' : 'Original Requested Date'}</span>
                        </button>
                      )}
                    </div>
                  
                  <div className="pt-2 border-t border-astro-border/50 mt-2">
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">{isHi ? 'पसंदीदा तारीख (वैकल्पिक)' : 'Preferred Date (Optional)'}</label>
                        {preferredDate && (
                          <button onClick={() => setPreferredDate('')} className="text-[10px] text-rose-500 font-bold hover:underline">Clear</button>
                        )}
                      </div>
                      <input 
                        type="date"
                        value={preferredDate}
                        onChange={(e) => setPreferredDate(e.target.value)}
                        className={`w-full bg-white border rounded-xl px-4 py-2.5 text-sm font-bold outline-none transition-all ${
                          preferredDate ? 'border-astro-accent text-astro-accent bg-astro-accent/5' : 'border-astro-border text-astro-primary'
                        }`}
                      />
                      <p className="text-[9px] text-astro-secondary-text leading-tight mt-1">
                        {isHi ? 'यदि चुना गया, तो हम विशेष रूप से इस तारीख के सर्वश्रेष्ठ समय पर ध्यान केंद्रित करेंगे।' : 'If selected, we will focus specifically on finding the best time for this particular day.'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">{isHi ? 'समय वरीयता' : 'Preferred Time of Day'}</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'any', label: isHi ? 'किसी भी समय' : 'Any Time', icon: Globe },
                        { id: 'morning', label: isHi ? 'सुबह' : 'Morning', icon: Sparkles },
                        { id: 'afternoon', label: isHi ? 'दोपहर' : 'Afternoon', icon: Calendar },
                        { id: 'evening', label: isHi ? 'शाम' : 'Evening', icon: Clock },
                      ].map(pref => (
                        <button
                          key={pref.id}
                          onClick={() => setTimePreference(pref.id as any)}
                          className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl border transition-all ${
                            timePreference === pref.id ? 'bg-astro-primary border-astro-primary text-white' : 'bg-white border-astro-border text-astro-secondary-text'
                          }`}
                        >
                          <pref.icon className="w-4 h-4" />
                          <span className="text-[10px] font-bold">{pref.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Calculation Framework Focus Selector (Combo, Rashi, Navtara) */}
                  <div className="space-y-1.5 pt-2 border-t border-astro-border/50">
                    <label className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">
                      {isHi ? 'गणना आधार (Aligning Framework)' : 'Alignment Engine & Basis'}
                    </label>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setPrimaryFocus('combo')}
                        className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                          primaryFocus === 'combo'
                            ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                            : 'bg-white border-astro-border text-astro-primary hover:bg-astro-card'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>{isHi ? 'कॉम्बो (संयुक्त)' : 'Combo (Combined)'}</span>
                        </div>
                        <p className={`text-[10px] mt-0.5 leading-tight ${primaryFocus === 'combo' ? 'text-white/80' : 'text-astro-secondary-text'}`}>
                          {isHi ? 'पंचांग शुद्धि + नवतारा + राशि बल' : 'Panchang + Navtara + Rashi'}
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPrimaryFocus('nakshatra')}
                        className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                          primaryFocus === 'nakshatra'
                            ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                            : 'bg-white border-astro-border text-astro-primary hover:bg-astro-card'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <Sparkles className="w-3.5 h-3.5" />
                          <span>{isHi ? 'नवतारा / नक्षत्र' : 'Navtara Based'}</span>
                        </div>
                        <p className={`text-[10px] mt-0.5 leading-tight ${primaryFocus === 'nakshatra' ? 'text-white/80' : 'text-astro-secondary-text'}`}>
                          {isHi ? 'तारा बल व ९-गुना ऊर्जा' : 'Tara Bala & 9-fold energy'}
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setPrimaryFocus('rashi')}
                        className={`p-2.5 rounded-xl border text-left transition-all cursor-pointer ${
                          primaryFocus === 'rashi'
                            ? 'bg-astro-primary text-white border-astro-primary shadow-xs'
                            : 'bg-white border-astro-border text-astro-primary hover:bg-astro-card'
                        }`}
                      >
                        <div className="flex items-center gap-1.5 font-bold text-xs">
                          <Compass className="w-3.5 h-3.5" />
                          <span>{isHi ? 'राशि व दृक गोचर' : 'Rashi Based'}</span>
                        </div>
                        <p className={`text-[10px] mt-0.5 leading-tight ${primaryFocus === 'rashi' ? 'text-white/80' : 'text-astro-secondary-text'}`}>
                          {isHi ? 'चंद्र राशि व दृक पंचांग' : 'Chandra Rashi & Drik'}
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* Advanced Vedic Shuddhi & Lagna Modality Dropdown */}
                  <div className="pt-3 border-t border-astro-border/50 flex flex-wrap items-center justify-between gap-3">
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-astro-accent" />
                        <span className="text-xs font-bold text-astro-primary">
                          {isHi ? 'वैदिक शुद्धि एवं नवतारा चक्र' : 'Vedic Shuddhi & Navatara Alignment'}
                        </span>
                      </div>
                      <p className="text-[10px] text-astro-secondary-text">
                        {isHi 
                          ? 'स्थिर/चर लग्न, अष्टम शुद्धि, एवं विशेष नवतारा (क्षेम, साधक, सम्पत्) चयन' 
                          : 'Sthira/Chara Lagna, 8H Ashtama Shuddhi, and targeted Navatara (Kshema, Sadhaka, Sampat)'}
                      </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      <NavataraFilterDropdown
                        selectedFilter={selectedTaraFilter}
                        customTaras={customTaraTypes}
                        onChange={(newFilter, newCustom) => {
                          setSelectedTaraFilter(newFilter);
                          if (newCustom) setCustomTaraTypes(newCustom);
                          try {
                            localStorage.setItem(taraFilterStorageKey, newFilter);
                          } catch {}
                        }}
                        janmaNakIdx={janmaNakIdx}
                        partnerCount={selectedPartners.length}
                        lang={isHi ? 'hi' : 'en'}
                      />

                      <VedicShuddhiDropdown 
                        options={shuddhiOptions}
                        onChange={(newOpts) => {
                          setShuddhiOptions(newOpts);
                          setIsUserCustomizedShuddhi(true);
                          try {
                            localStorage.setItem(shuddhiStorageKey, JSON.stringify(newOpts));
                          } catch {}
                        }}
                        lang={isHi ? 'hi' : 'en'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit / Find Best Muhurta Button */}
              <div className="pt-8 border-t border-astro-border/60 flex flex-col items-center justify-center gap-3" id="calculate-section">
                {!selectedMilestone && (
                  <div className="text-xs text-amber-700 font-semibold flex items-center gap-1.5 bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    {isHi ? 'कृपया आगे बढ़ने के लिए ऊपर एक विशिष्ट गतिविधि चुनें' : 'Please select an activity milestone above'}
                  </div>
                )}
                {locations.length === 0 && (
                  <div className="text-xs text-amber-700 font-semibold flex items-center gap-1.5 bg-amber-50 border border-amber-200 px-4 py-2 rounded-xl">
                    <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                    {isHi ? 'कृपया कम से कम एक स्थान चुनें' : 'Please select at least one location'}
                  </div>
                )}
                <button 
                  id="calculate-muhurta-btn"
                  onClick={() => handleSearch()}
                  disabled={isCalculating || !selectedMilestone || locations.length === 0}
                  className={`rounded-xl px-12 py-3.5 font-bold text-sm flex items-center gap-2 transition-all shadow-md transform active:scale-95 cursor-pointer ${
                    isCalculating || !selectedMilestone || locations.length === 0
                      ? 'bg-astro-border/80 text-astro-secondary-text/60 cursor-not-allowed shadow-none'
                      : 'bg-astro-primary hover:bg-astro-secondary text-white'
                  }`}
                >
                  {isCalculating ? <Clock className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
                  {isHi ? 'सर्वश्रेष्ठ मुहूर्त खोजें' : 'FIND BEST MUHURTA'}
                </button>
              </div>
            </div>
            </div>
          )}

          {currentStep === 'results' && (
            <div className="animate-in fade-in duration-300 space-y-6" id="results-step">
              
              {/* Calculating State */}
              {isCalculating ? (
                <div className="py-24 text-center space-y-4">
                  <div className="w-16 h-16 bg-astro-card rounded-full flex items-center justify-center mx-auto border border-astro-border animate-spin">
                    <SwastikLogo size="md" />
                  </div>
                  <h3 className="text-lg font-bold text-astro-primary font-serif">{isHi ? 'सर्वश्रेष्ठ मुहूर्त की गणना की जा रही है...' : 'Aligning Vedic Factors...'}</h3>
                  <p className="text-xs text-astro-secondary-text max-w-sm mx-auto leading-relaxed">
                    {isHi 
                      ? 'हम प्रत्येक 15 मिनट के अंतराल पर ग्रहों, नक्षत्रों, और चौघड़िया का तारा बल के साथ विश्लेषण कर रहे हैं।' 
                      : 'Scanning daily planetary paths, and mapping Tara Balas to provide a highly calibrated auspicious index.'}
                  </p>
                  <button 
                    onClick={stopCalculation}
                    className="text-xs text-rose-500 font-bold underline hover:text-rose-700 mt-2"
                  >
                    Cancel Scan
                  </button>
                </div>
              ) : Object.keys(resultsByLocation).some(k => resultsByLocation[k].length > 0) ? (
                <div className="space-y-4 max-w-4xl mx-auto">
                  {/* Subtle In-Place Updating Pill */}
                  {isUpdatingResults && (
                    <div className="sticky top-2 z-40 bg-amber-900/95 backdrop-blur-xs text-white text-xs font-bold px-4 py-2 rounded-xl shadow-lg flex items-center justify-center gap-2 mx-auto w-fit border border-amber-600/40 animate-in fade-in slide-in-from-top-1">
                      <Sparkles className="w-3.5 h-3.5 text-amber-300 animate-spin" />
                      <span>{isHi ? 'वैदिक शुद्धि एवं नवतारा अनुसार पुनः परिकलन जारी...' : 'Recalculating alignments with updated Vedic Shuddhi & Navatara filters...'}</span>
                    </div>
                  )}

                  {/* Option to Go Back to Original Requested Date */}
                  {isDatesModifiedFromOriginal && originalSearchDates && (
                    <div className="bg-gradient-to-r from-amber-500/15 via-white to-amber-500/10 border border-amber-300 rounded-2xl p-4 shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 animate-in fade-in slide-in-from-top-2">
                      <div className="flex items-start sm:items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-amber-500/20 text-amber-900 border border-amber-300 flex items-center justify-center shrink-0 shadow-2xs mt-0.5 sm:mt-0">
                          <RotateCcw className="w-5 h-5 text-amber-900" />
                        </div>
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs sm:text-sm font-bold text-astro-primary font-serif">
                              {isHi ? 'भविष्य की सुझाई गई मुहूर्त तिथि देख रहे हैं' : 'Viewing Recommended Future Muhurta Date'}
                            </span>
                            <span className="text-[10px] font-bold bg-amber-200 text-amber-950 px-2.5 py-0.5 rounded-full border border-amber-300">
                              {new Date(startDate).toLocaleDateString(isHi ? 'hi-IN' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </span>
                          </div>
                          <p className="text-[11px] text-astro-secondary-text mt-0.5">
                            {isHi 
                              ? `मूल अनुरोधित तारीख: ${originalSearchDates.preferredDate ? new Date(originalSearchDates.preferredDate).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : `${new Date(originalSearchDates.startDate).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short' })} से ${new Date(originalSearchDates.endDate).toLocaleDateString('hi-IN', { day: 'numeric', month: 'short', year: 'numeric' })}`}`
                              : `Originally requested date: ${originalSearchDates.preferredDate ? new Date(originalSearchDates.preferredDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }) : `${new Date(originalSearchDates.startDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} to ${new Date(originalSearchDates.endDate).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}`}`}
                          </p>
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={handleRevertToOriginalDate}
                        className="px-4 py-2.5 rounded-xl bg-amber-900 hover:bg-amber-950 text-white font-bold text-xs transition-all flex items-center justify-center gap-2 shadow-sm cursor-pointer shrink-0 self-start sm:self-auto"
                        title={isHi ? 'मूल अनुरोधित तारीख पर वापस जाएं' : 'Go back to original requested date'}
                      >
                        <RotateCcw className="w-3.5 h-3.5 text-amber-300" />
                        <span>{isHi ? 'मूल अनुरोधित तारीख पर वापस जाएं' : 'Back to Original Date'}</span>
                      </button>
                    </div>
                  )}

                  {/* Active Activity & Selected Variables Summary Banner with Quick Edit */}
                  <div className="bg-white border border-astro-border/90 rounded-2xl p-4 shadow-xs space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border/50 pb-2.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-astro-secondary-text">
                          {isHi ? 'मुहूर्त उद्देश्य' : 'Muhurta Purpose'}:
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-astro-primary font-serif flex items-center gap-1.5">
                          ✨ {activeMilestone?.label || selectedMilestone}
                        </span>
                        {selectedCategory && (
                          <span className="text-[10px] font-semibold bg-astro-primary/5 text-astro-accent px-2 py-0.5 rounded-md border border-astro-border">
                            {MUHURTA_TAXONOMY.find(c => c.id === selectedCategory)?.label || selectedCategory}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap items-center gap-2">
                        {/* Navatara Filter Dropdown in Results Bar */}
                        <NavataraFilterDropdown
                          selectedFilter={selectedTaraFilter}
                          customTaras={customTaraTypes}
                          onChange={(newFilter, newCustom) => {
                            setSelectedTaraFilter(newFilter);
                            if (newCustom) setCustomTaraTypes(newCustom);
                            try {
                              localStorage.setItem(taraFilterStorageKey, newFilter);
                            } catch {}
                            handleSearch(primaryFocus, undefined, undefined, shuddhiOptions, newFilter, newCustom);
                          }}
                          janmaNakIdx={janmaNakIdx}
                          partnerCount={selectedPartners.length}
                          lang={isHi ? 'hi' : 'en'}
                          compact={true}
                        />

                        {/* Vedic Shuddhi Settings Dropdown in Results Bar */}
                        <VedicShuddhiDropdown 
                          options={shuddhiOptions}
                          onChange={(newOpts) => {
                            setShuddhiOptions(newOpts);
                            setIsUserCustomizedShuddhi(true);
                            try {
                              localStorage.setItem(shuddhiStorageKey, JSON.stringify(newOpts));
                            } catch {}
                            handleSearch(primaryFocus, undefined, undefined, newOpts, selectedTaraFilter);
                          }}
                          lang={isHi ? 'hi' : 'en'}
                          compact={true}
                        />

                        {/* Combo vs Navtara vs Rashi Focus View Toggle */}
                        <div className="inline-flex items-center bg-astro-card border border-astro-border rounded-lg p-0.5 text-[11px] font-bold">
                          <button
                            onClick={() => {
                              setPrimaryFocus('combo');
                              handleSearch('combo', undefined, undefined, shuddhiOptions, selectedTaraFilter);
                            }}
                            className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                              primaryFocus === 'combo'
                                ? 'bg-astro-primary text-white shadow-2xs'
                                : 'text-astro-secondary-text hover:text-astro-primary'
                            }`}
                            title="Combined Vedic Panchang + Navtara + Rashi"
                          >
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Combo</span>
                          </button>
                          <button
                            onClick={() => {
                              setPrimaryFocus('nakshatra');
                              handleSearch('nakshatra', undefined, undefined, shuddhiOptions, selectedTaraFilter);
                            }}
                            className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                              primaryFocus === 'nakshatra'
                                ? 'bg-astro-primary text-white shadow-2xs'
                                : 'text-astro-secondary-text hover:text-astro-primary'
                            }`}
                            title="Vedic Nakshatra / Tara Bala Emphasis"
                          >
                            <Sparkles className="w-3 h-3" />
                            <span>Navtara</span>
                          </button>
                          <button
                            onClick={() => {
                              setPrimaryFocus('rashi');
                              handleSearch('rashi', undefined, undefined, shuddhiOptions, selectedTaraFilter);
                            }}
                            className={`px-2.5 py-1 rounded-md transition-all flex items-center gap-1 cursor-pointer ${
                              primaryFocus === 'rashi'
                                ? 'bg-astro-primary text-white shadow-2xs'
                                : 'text-astro-secondary-text hover:text-astro-primary'
                            }`}
                            title="Drik Panchang / Chandra Rashi & Gochar Emphasis"
                          >
                            <Compass className="w-3 h-3" />
                            <span>Rashi</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => setShowScoreExplanationModal(true)}
                            className="px-1.5 py-1 text-astro-secondary-text hover:text-astro-accent rounded-md hover:bg-white/60 transition-all cursor-pointer ml-0.5"
                            title={isHi ? 'संयुक्त (Combo) अंक नवतारा एवं राशि से भिन्न क्यों है? समझें' : 'Why does Combined score differ from Navtara & Rashi?'}
                          >
                            <HelpCircle className="w-3.5 h-3.5 text-astro-accent" />
                          </button>
                        </div>

                        <button 
                          onClick={restartSearch}
                          className="text-xs font-bold text-astro-primary hover:underline bg-astro-card border border-astro-border px-3 py-1.5 rounded-lg transition-all cursor-pointer"
                        >
                          {isHi ? 'पुनः प्रारंभ करें' : 'Start Over'}
                        </button>
                      </div>
                    </div>

                    {/* Quick Variable Badges & Direct Edit Triggers */}
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-astro-secondary-text pt-0.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-astro-secondary-text/80">Selected Variables:</span>
                        <button
                          onClick={() => setCurrentStep('gathering')}
                          className="inline-flex items-center gap-1 bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2 py-0.5 rounded-md text-[11px] font-medium transition-all group cursor-pointer"
                          title="Click to edit activity"
                        >
                          <span>Activity: <strong>{activeMilestone?.label || selectedMilestone}</strong></span>
                          <Edit2 className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />
                        </button>

                        <button
                          onClick={() => setCurrentStep('gathering')}
                          className="inline-flex items-center gap-1 bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2 py-0.5 rounded-md text-[11px] font-medium transition-all group cursor-pointer"
                          title="Click to edit location"
                        >
                          <MapPin className="w-2.5 h-2.5 text-astro-accent" />
                          <span>{locations[0]?.name || 'Location'} ({locations.length})</span>
                          <Edit2 className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />
                        </button>

                        <button
                          onClick={() => setCurrentStep('gathering')}
                          className="inline-flex items-center gap-1 bg-astro-card hover:bg-astro-primary/10 text-astro-primary border border-astro-border px-2 py-0.5 rounded-md text-[11px] font-medium transition-all group cursor-pointer"
                          title="Click to edit scan range"
                        >
                          <Calendar className="w-2.5 h-2.5 text-astro-accent" />
                          <span>{startDate} to {endDate} ({timePreference})</span>
                          <Edit2 className="w-2.5 h-2.5 opacity-60 group-hover:opacity-100" />
                        </button>

                        {isDatesModifiedFromOriginal && originalSearchDates && (
                          <button
                            type="button"
                            onClick={handleRevertToOriginalDate}
                            className="inline-flex items-center gap-1.5 bg-amber-100 hover:bg-amber-200 text-amber-950 border border-amber-300 px-2.5 py-0.5 rounded-md text-[11px] font-bold transition-all shadow-2xs cursor-pointer"
                            title={isHi ? 'मूल अनुरोधित तारीख पर वापस जाएं' : 'Return to originally requested dates'}
                          >
                            <RotateCcw className="w-3 h-3 text-amber-800" />
                            <span>{isHi ? 'मूल तारीख पर वापस जाएं' : 'Back to Original Date'}</span>
                          </button>
                        )}
                      </div>

                      <span className="text-[10px] text-astro-secondary-text/70 italic">
                        {primaryFocus === 'rashi' 
                          ? (isHi ? 'चंद्र राशि एवं गोचर आधारित मूल्यांकन सक्रिय' : 'Using Chandra Rashi & Gochar House Alignment') 
                          : primaryFocus === 'nakshatra'
                          ? (isHi ? 'वैदिक नवतारा एवं तारा बल आधारित मूल्यांकन सक्रिय' : 'Using Vedic Navtara & Tara Bala Alignment')
                          : (isHi ? 'पंचम महापंचक, नवतारा एवं राशि संयुक्त मूल्यांकन सक्रिय' : 'Using Balanced Combo (Panchang + Navtara + Rashi)')}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center justify-between gap-2 pb-1">
                    <div>
                      <h3 className="text-sm font-bold text-astro-primary font-serif">
                        {isHi ? 'सर्वश्रेष्ठ शुभ मुहूर्त अंतराल' : 'Calibrated Auspicious Muhurta Windows'}
                      </h3>
                      <p className="text-[11px] text-astro-secondary-text">
                        {isHi ? 'विवरण देखने या छुपाने के लिए किसी भी कार्ड पर क्लिक करें।' : 'Click any window card to expand its astrological alignment in-place.'}
                      </p>
                    </div>

                    {/* Primary Results Sort Toggle (ACT-MUH-01) */}
                    <div
                      className="inline-flex bg-white/90 border border-amber-200 rounded-xl p-1 text-[11px] font-bold shadow-2xs"
                      role="group"
                      aria-label={isHi ? 'मुहूर्त क्रमबद्धता चयन' : 'Muhurta sorting selection'}
                    >
                      <button
                        type="button"
                        role="radio"
                        aria-checked={sortOrder === 'score'}
                        onClick={() => setSortOrder('score')}
                        className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                          sortOrder === 'score'
                            ? 'bg-amber-600 text-white shadow-2xs'
                            : 'text-astro-secondary-text hover:text-amber-800'
                        }`}
                        aria-label={isHi ? 'सभी मुहूर्त अंक अनुसार क्रमबद्ध करें (सर्वोच्च पहले)' : 'Sort all Muhurtas by score (highest first)'}
                      >
                        <Award className="w-3 h-3" />
                        <span>{isHi ? 'अंक अनुसार (सर्वोच्च)' : 'By Score (Highest)'}</span>
                      </button>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={sortOrder === 'date'}
                        onClick={() => setSortOrder('date')}
                        className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                          sortOrder === 'date'
                            ? 'bg-amber-600 text-white shadow-2xs'
                            : 'text-astro-secondary-text hover:text-amber-800'
                        }`}
                        aria-label={isHi ? 'सभी मुहूर्त तिथि अनुसार क्रमबद्ध करें (सबसे पहले वाला पहले)' : 'Sort all Muhurtas by date (earliest first)'}
                      >
                        <Calendar className="w-3 h-3" />
                        <span>{isHi ? 'तिथि अनुसार (कालक्रम)' : 'By Date (Earliest)'}</span>
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3" id="results-list">
                    {locations.map((loc, lIdx) => {
                      const rawRes = resultsByLocation[loc.display_name] || [];
                      if (rawRes.length === 0) return null;

                      const res = [...rawRes].sort((a, b) => compareMuhurtas(a, b, sortOrder));

                      return (
                        <div key={lIdx} className="space-y-2.5">
                          {locations.length > 1 && (
                            <div className="text-[11px] font-bold text-astro-accent uppercase tracking-wider px-1 pt-1 flex items-center gap-1.5">
                              <MapPin className="w-3.5 h-3.5" /> {loc.name || loc.display_name}
                            </div>
                          )}
                          {(() => {
                            const now = new Date();
                            // Find the first upcoming window index
                            const firstUpcomingIdx = res.findIndex(item => {
                              const endObj = item.endTime ? new Date(item.endTime) : (item.startTime ? new Date(item.startTime) : new Date(item.dateTime));
                              return endObj.getTime() > now.getTime();
                            });

                            return res.map((item, rIdx) => {
                              // Stable per-window identity so expansion survives re-sorting
                              const cardId = `${lIdx}-${item.startTime || item.dateTime || item.evaluatedMoment}`;
                              const isSelected = detailedViewId === cardId;
                              const mDate = new Date(item.dateTime);
                              const startDateObj = item.startTime ? new Date(item.startTime) : mDate;
                              const endDateObj = item.endTime ? new Date(item.endTime) : mDate;
                              const isMultiDay = startDateObj.toDateString() !== endDateObj.toDateString();

                              const isPast = endDateObj.getTime() < now.getTime();
                              const isLiveNow = startDateObj.getTime() <= now.getTime() && endDateObj.getTime() >= now.getTime();
                              const isNextBestUpcoming = rIdx === firstUpcomingIdx && firstUpcomingIdx > 0;

                              return (
                                <div 
                                  key={cardId}
                                  id={`muhurta-window-${cardId}`}
                                  className={`rounded-2xl border transition-all overflow-hidden relative scroll-mt-24 sm:scroll-mt-28 ${
                                    isNextBestUpcoming
                                      ? 'ring-2 ring-emerald-500 border-emerald-500 shadow-md bg-white'
                                      : isLiveNow
                                      ? 'ring-2 ring-amber-500 border-amber-500 shadow-md bg-white'
                                      : isSelected 
                                      ? 'border-astro-primary shadow-lg bg-white ring-1 ring-astro-primary/20' 
                                      : isPast
                                      ? 'border-astro-border/70 bg-white/70 opacity-80 hover:opacity-100 hover:border-astro-accent/50'
                                      : 'border-astro-border bg-white hover:border-astro-accent/50 hover:shadow-sm'
                                  }`}
                                >
                                  {/* Next Best Upcoming & Live Badges */}
                                  {isNextBestUpcoming && (
                                    <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-[10px] font-extrabold px-3 py-1 flex items-center justify-between shadow-xs">
                                      <span className="flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5 text-emerald-200 animate-pulse" />
                                        {isHi ? '👉 अगला सर्वश्रेष्ठ उपलब्ध मुहूर्त (समय बीत जाने पर)' : '🌟 NEXT BEST UPCOMING OPTION (Recommended)'}
                                      </span>
                                      <span className="bg-white/20 text-white text-[9px] px-1.5 py-0.2 rounded font-semibold">
                                        {isHi ? 'सक्रिय एवं आगामी' : 'Upcoming'}
                                      </span>
                                    </div>
                                  )}

                                  {isLiveNow && (
                                    <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white text-[10px] font-extrabold px-3 py-1 flex items-center justify-between shadow-xs">
                                      <span className="flex items-center gap-1.5">
                                        <div className="w-2 h-2 rounded-full bg-white animate-ping" />
                                        {isHi ? '🔥 वर्तमान में सक्रिय शुभ मुहूर्त (LIVE NOW)' : '🔥 CURRENTLY ACTIVE WINDOW (LIVE NOW)'}
                                      </span>
                                      <span className="bg-white/20 text-white text-[9px] px-1.5 py-0.2 rounded font-semibold">
                                        {isHi ? 'जारी है' : 'In Progress'}
                                      </span>
                                    </div>
                                  )}

                                  {/* Accordion Header Card */}
                                  <button
                                    type="button"
                                    id={`muhurta-header-${lIdx}-${rIdx}`}
                                    aria-expanded={isSelected}
                                    aria-controls={`muhurta-details-${lIdx}-${rIdx}`}
                                    aria-label={isSelected ? (isHi ? 'मुहूर्त विवरण बंद करें' : 'Collapse muhurta details') : (isHi ? 'मुहूर्त विवरण देखें' : 'View muhurta details')}
                                    onClick={() => {
                                      if (isSelected) {
                                        setDetailedViewId(null);
                                      } else {
                                        setDetailedViewId(cardId);
                                        setTimeout(() => {
                                          const el = document.getElementById(`muhurta-window-${cardId}`);
                                          if (el) {
                                            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                          }
                                        }, 50);
                                      }
                                    }}
                                    className={`w-full p-4.5 sm:p-5 text-left transition-all relative flex items-center justify-between gap-3 ${
                                      isSelected 
                                        ? 'bg-astro-primary text-white' 
                                        : 'bg-white text-astro-text hover:bg-astro-card/50'
                                    }`}
                                  >
                                    <div className="space-y-1.5 flex-1 min-w-0 pr-2">
                                      <div className="text-xs sm:text-sm font-bold flex flex-wrap items-center justify-between gap-2">
                                        <span className="font-serif font-bold text-sm sm:text-base tracking-wide flex items-center gap-2">
                                          <Calendar className={`w-4 h-4 shrink-0 ${isSelected ? 'text-astro-accent' : isNextBestUpcoming ? 'text-emerald-700' : 'text-astro-primary'}`} />
                                          {isMultiDay ? `${formatDate(startDateObj)} — ${formatDate(endDateObj)}` : formatDate(startDateObj)}
                                          {isPast && (
                                            <span className="text-[10px] font-sans font-bold bg-gray-100 text-gray-500 border border-gray-200 px-1.5 py-0.2 rounded">
                                              {isHi ? 'समय समाप्त' : 'Time Elapsed'}
                                            </span>
                                          )}
                                        </span>
                                        <span className={`text-[10px] sm:text-xs px-2.5 py-0.5 rounded-full border font-bold shrink-0 shadow-xs ${
                                          isSelected 
                                            ? 'bg-white/15 border-white/25 text-white' 
                                            : getScoreColor(item.score.label)
                                        }`}>
                                          {item.score.totalScore}% - {item.score.label}
                                        </span>
                                      </div>

                                    <div className={`text-xs sm:text-sm font-semibold flex items-center gap-2 ${isSelected ? 'text-white/95' : 'text-astro-primary'}`}>
                                      <Clock className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-astro-accent' : 'text-astro-accent'}`} />
                                      {item.startTime && item.endTime ? (
                                        isMultiDay ? (
                                          <span>
                                            {startDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, {formatTime(startDateObj, false)} — {endDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, {formatTime(endDateObj, false)}
                                          </span>
                                        ) : (
                                          <span>
                                            {formatTime(startDateObj, false)} — {formatTime(endDateObj, false)}
                                          </span>
                                        )
                                      ) : (
                                        <span>{formatSmartDateTime(mDate, false)}</span>
                                      )}
                                    </div>

                                    <div className={`text-[10px] sm:text-xs ${isSelected ? 'text-white/75' : 'text-astro-secondary-text'} flex flex-wrap items-center gap-x-2 gap-y-0.5`}>
                                      <span><strong>Tithi:</strong> {item.panchang.tithi}</span>
                                      <span>&bull;</span>
                                      {primaryFocus === 'rashi' ? (
                                        <span>
                                          <strong>Moon Rashi:</strong> {item.panchang.moonRashi}
                                          <span className={`ml-1 font-semibold ${isSelected ? 'text-amber-200' : 'text-astro-primary'}`}>
                                            ({item.panchang.nakshatra})
                                          </span>
                                        </span>
                                      ) : (
                                        <span>
                                          <strong>Nakshatra:</strong> {item.panchang.nakshatra}
                                          {item.panchang.nakshatraType && (
                                            <span className={`ml-1 font-semibold ${isSelected ? 'text-amber-200' : 'text-astro-primary'}`}>
                                              [{item.panchang.nakshatraType.category.split(' ')[0]}]
                                            </span>
                                          )}
                                        </span>
                                      )}
                                      {item.taraBala?.taraName && (
                                        <>
                                          <span>&bull;</span>
                                          <span className="font-semibold">{item.taraBala.taraName} ({item.taraBala.suitability})</span>
                                        </>
                                      )}
                                    </div>

                                    {item.lagnaDetails && (
                                      <div className="flex flex-wrap items-center gap-1.5 pt-1 w-full">
                                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-md border ${
                                          isSelected
                                            ? 'bg-white/15 text-white border-white/20'
                                            : item.lagnaDetails.modality === 'Sthira'
                                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                            : item.lagnaDetails.modality === 'Chara'
                                            ? 'bg-blue-50 text-blue-800 border-blue-200'
                                            : 'bg-amber-50 text-amber-800 border-amber-200'
                                        }`}>
                                          <Building2 className="w-3 h-3 shrink-0" />
                                          <span>Lagna: {item.lagnaDetails.sign} ({item.lagnaDetails.modalityLabel.split(' ')[0]})</span>
                                        </span>

                                        {item.lagnaDetails.isAshtamaClean ? (
                                          <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                            isSelected ? 'bg-emerald-400/20 text-emerald-200 border border-emerald-400/30' : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                          }`}>
                                            ✓ 8H Clean
                                          </span>
                                        ) : (
                                          <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                            isSelected ? 'bg-red-400/20 text-red-200 border border-red-400/30' : 'bg-red-50 text-red-700 border border-red-200'
                                          }`}>
                                            ⚠️ 8H Occupied
                                          </span>
                                        )}

                                        {item.specialYogas && item.specialYogas.length > 0 && (
                                          <span className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                                            isSelected ? 'bg-amber-400/20 text-amber-200 border border-amber-400/30' : 'bg-amber-50 text-amber-800 border border-amber-200'
                                          }`}>
                                            <Sparkles className="w-2.5 h-2.5" />
                                            {item.specialYogas[0].name}
                                          </span>
                                        )}
                                      </div>
                                    )}
                                  </div>

                                  <div className="flex items-center gap-2 shrink-0">
                                    <span className={`text-[11px] font-bold hidden sm:inline-block ${isSelected ? 'text-astro-accent' : 'text-astro-secondary-text'}`}>
                                      {isSelected ? (isHi ? 'बंद करें' : 'Collapse') : (isHi ? 'विवरण देखें' : 'View Details')}
                                    </span>
                                    <div className={`w-7 h-7 rounded-full flex items-center justify-center transition-transform duration-200 ${
                                      isSelected ? 'bg-white/15 text-white rotate-180' : 'bg-astro-card text-astro-secondary-text hover:text-astro-primary'
                                    }`}>
                                      <ChevronDown className="w-4 h-4" />
                                    </div>
                                  </div>
                                </button>

                                {/* In-Place Expanded Detail Body */}
                                {isSelected && (
                                  <div
                                    id={`muhurta-details-${lIdx}-${rIdx}`}
                                    role="region"
                                    aria-labelledby={`muhurta-header-${lIdx}-${rIdx}`}
                                    className="border-t border-astro-border bg-[#FAF8F5] p-5 sm:p-7 space-y-6 animate-in fade-in slide-in-from-top-2 duration-300"
                                  >
                                    {/* Header Highlight Banner */}
                                    <div className="bg-white border border-astro-border rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 shadow-xs">
                                      <div className="space-y-1">
                                        <div className="text-[10px] uppercase font-bold tracking-widest text-astro-secondary-text">
                                          {isHi ? 'मुहूर्त अवधि' : 'Auspicious Window Period'}
                                        </div>
                                        <div className="text-sm font-bold text-astro-primary font-serif">
                                          {isMultiDay 
                                            ? `${startDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${formatTime(startDateObj, false)} — ${endDateObj.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}, ${formatTime(endDateObj, false)}`
                                            : `${formatTime(startDateObj, false)} — ${formatTime(endDateObj, false)}`}
                                        </div>
                                        {item.transitInfo?.summaryNote && (
                                          <p className="text-xs text-emerald-800 font-medium">
                                            ✨ {item.transitInfo.summaryNote}
                                          </p>
                                        )}
                                      </div>

                                      <div className="text-center bg-astro-card px-4 py-2 rounded-xl border border-astro-border shrink-0">
                                        <span className="text-[9px] uppercase tracking-widest text-astro-secondary-text block font-bold">Auspicious score</span>
                                        <span className="text-xl font-bold font-serif text-astro-primary block">{item.score.totalScore}%</span>
                                        <button
                                          type="button"
                                          onClick={() => setShowScoreExplanationModal(true)}
                                          className="text-[10px] text-astro-secondary-text hover:text-astro-accent font-semibold underline mt-0.5 block cursor-pointer transition-colors"
                                        >
                                          {isHi ? 'अंक विश्लेषण' : 'Score Details'}
                                        </button>
                                      </div>
                                    </div>

                                    {/* 4-Pillar Classical Score Breakdown */}
                                    {item.score.pillars && (
                                      <div className="bg-astro-card/40 border border-astro-border/70 rounded-xl p-3 sm:p-3.5 space-y-2.5">
                                        <div className="flex items-center justify-between">
                                          <span className="text-[11px] font-bold text-astro-primary flex items-center gap-1.5">
                                            <Sparkles className="w-3.5 h-3.5 text-astro-accent" />
                                            {isHi ? 'शास्त्रीय घटक अंक (Individual Pillar Scores)' : 'Classical Muhurta Pillars (Individual Scores)'}
                                          </span>
                                          <span className="text-[10px] text-astro-secondary-text font-medium">
                                            {isHi ? 'प्रत्येक अंग का स्वतंत्र मूल्यांकन' : 'Granular Component Evaluation'}
                                          </span>
                                        </div>

                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                                          {/* 1. Tara Bala Pillar */}
                                          <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs space-y-1.5">
                                            <div className="flex items-center justify-between text-[10px]">
                                              <span className="font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                                                🌟 {isHi ? 'तारा बल' : 'Tara Bala'}
                                              </span>
                                              <span className="font-bold text-astro-primary font-mono">
                                                {item.score.pillars.taraBala.score}/{item.score.pillars.taraBala.max}
                                              </span>
                                            </div>
                                            <div className="w-full bg-astro-bg rounded-full h-1.5 overflow-hidden">
                                              <div 
                                                className={`h-full rounded-full ${
                                                  item.score.pillars.taraBala.percentage >= 80 ? 'bg-emerald-500' :
                                                  item.score.pillars.taraBala.percentage >= 60 ? 'bg-blue-500' :
                                                  item.score.pillars.taraBala.percentage >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                                                }`} 
                                                style={{ width: `${item.score.pillars.taraBala.percentage}%` }}
                                              />
                                            </div>
                                            <div className="flex items-center justify-between text-[10px] pt-0.5">
                                              <span className="font-semibold text-astro-primary truncate max-w-[90px]">{item.score.pillars.taraBala.label}</span>
                                              <span className="font-bold text-emerald-700 shrink-0 font-mono">{item.score.pillars.taraBala.percentage}%</span>
                                            </div>
                                          </div>

                                          {/* 2. Vedic Lagna & Shuddhi */}
                                          <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs space-y-1.5">
                                            <div className="flex items-center justify-between text-[10px]">
                                              <span className="font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                                                🏛️ {isHi ? 'लग्न शुद्धि' : 'Vedic Lagna'}
                                              </span>
                                              <span className="font-bold text-astro-primary font-mono">
                                                {item.score.pillars.lagnaShuddhi.score}/{item.score.pillars.lagnaShuddhi.max}
                                              </span>
                                            </div>
                                            <div className="w-full bg-astro-bg rounded-full h-1.5 overflow-hidden">
                                              <div 
                                                className={`h-full rounded-full ${
                                                  item.score.pillars.lagnaShuddhi.percentage >= 80 ? 'bg-emerald-500' :
                                                  item.score.pillars.lagnaShuddhi.percentage >= 60 ? 'bg-blue-500' :
                                                  item.score.pillars.lagnaShuddhi.percentage >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                                                }`} 
                                                style={{ width: `${item.score.pillars.lagnaShuddhi.percentage}%` }}
                                              />
                                            </div>
                                            <div className="flex items-center justify-between text-[10px] pt-0.5">
                                              <span className="font-semibold text-astro-primary truncate max-w-[90px]">{item.score.pillars.lagnaShuddhi.label}</span>
                                              <span className="font-bold text-emerald-700 shrink-0 font-mono">{item.score.pillars.lagnaShuddhi.percentage}%</span>
                                            </div>
                                          </div>

                                          {/* 3. Chandra Bala */}
                                          <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs space-y-1.5">
                                            <div className="flex items-center justify-between text-[10px]">
                                              <span className="font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                                                🌙 {isHi ? 'चंद्र बल' : 'Chandra Bala'}
                                              </span>
                                              <span className="font-bold text-astro-primary font-mono">
                                                {item.score.pillars.chandraBala.score}/{item.score.pillars.chandraBala.max}
                                              </span>
                                            </div>
                                            <div className="w-full bg-astro-bg rounded-full h-1.5 overflow-hidden">
                                              <div 
                                                className={`h-full rounded-full ${
                                                  item.score.pillars.chandraBala.percentage >= 80 ? 'bg-emerald-500' :
                                                  item.score.pillars.chandraBala.percentage >= 60 ? 'bg-blue-500' :
                                                  item.score.pillars.chandraBala.percentage >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                                                }`} 
                                                style={{ width: `${item.score.pillars.chandraBala.percentage}%` }}
                                              />
                                            </div>
                                            <div className="flex items-center justify-between text-[10px] pt-0.5">
                                              <span className="font-semibold text-astro-primary truncate max-w-[90px]">{item.score.pillars.chandraBala.label}</span>
                                              <span className="font-bold text-emerald-700 shrink-0 font-mono">{item.score.pillars.chandraBala.percentage}%</span>
                                            </div>
                                          </div>

                                          {/* 4. Panchanga Limbs */}
                                          <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs space-y-1.5">
                                            <div className="flex items-center justify-between text-[10px]">
                                              <span className="font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                                                ☀️ {isHi ? 'पंचांग अंग' : 'Panchanga Limbs'}
                                              </span>
                                              <span className="font-bold text-astro-primary font-mono">
                                                {item.score.pillars.panchangaLimbs.score}/{item.score.pillars.panchangaLimbs.max}
                                              </span>
                                            </div>
                                            <div className="w-full bg-astro-bg rounded-full h-1.5 overflow-hidden">
                                              <div 
                                                className={`h-full rounded-full ${
                                                  item.score.pillars.panchangaLimbs.percentage >= 80 ? 'bg-emerald-500' :
                                                  item.score.pillars.panchangaLimbs.percentage >= 60 ? 'bg-blue-500' :
                                                  item.score.pillars.panchangaLimbs.percentage >= 40 ? 'bg-amber-500' : 'bg-rose-500'
                                                }`} 
                                                style={{ width: `${item.score.pillars.panchangaLimbs.percentage}%` }}
                                              />
                                            </div>
                                            <div className="flex items-center justify-between text-[10px] pt-0.5">
                                              <span className="font-semibold text-astro-primary truncate max-w-[90px]">{item.score.pillars.panchangaLimbs.label}</span>
                                              <span className="font-bold text-emerald-700 shrink-0 font-mono">{item.score.pillars.panchangaLimbs.percentage}%</span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Shastric Action Guidance (Dual-Track Teleology: Lagna vs Chandra Bala) - Collapsed by default */}
                                    {(() => {
                                      const lagnaPct = item.score.pillars?.lagnaShuddhi?.percentage ?? 80;
                                      const taraPct = item.score.pillars?.taraBala?.percentage ?? 80;
                                      const chandraPct = item.score.pillars?.chandraBala?.percentage ?? 60;
                                      const isSthira = item.lagnaDetails?.modality === 'Sthira';
                                      const isAshtamaClean = item.lagnaDetails?.isAshtamaClean !== false;

                                      const transitMoonRashiIdx = ZODIAC_SIGNS.findIndex(z => z.toLowerCase() === (item.panchang.moonRashi || '').toLowerCase());
                                      const transitHouseFromJanma = janmaRashiIdx >= 0 && transitMoonRashiIdx >= 0
                                        ? ((transitMoonRashiIdx - janmaRashiIdx + 12) % 12) + 1
                                        : 1;

                                      const muhurtaLagnaIdx = ZODIAC_SIGNS.findIndex(z => z.toLowerCase() === (item.lagnaDetails?.sign || '').toLowerCase());
                                      const moonHouseInMuhurta = muhurtaLagnaIdx >= 0 && transitMoonRashiIdx >= 0
                                        ? ((transitMoonRashiIdx - muhurtaLagnaIdx + 12) % 12) + 1
                                        : 1;

                                      const isAshtamaChandra = transitHouseFromJanma === 8;
                                      const isDifficultChandra = isAshtamaChandra || transitHouseFromJanma === 6 || transitHouseFromJanma === 12 || transitHouseFromJanma === 4;
                                      const isBedrockStrong = lagnaPct >= 65 && taraPct >= 60 && isAshtamaClean;
                                      const isMoonInKendraOrTrikona = [1, 4, 7, 10, 5, 9, 11].includes(moonHouseInMuhurta);

                                      // Next favorable Rashi
                                      const nextRashiIdx = (transitMoonRashiIdx + 1) % 12;
                                      const nextRashiName = ZODIAC_SIGNS[nextRashiIdx];

                                      const guidanceKey = cardId;
                                      const isGuidanceExpanded = expandedGuidanceKeys.has(guidanceKey);

                                      return (
                                        <div className="bg-gradient-to-br from-white via-amber-50/20 to-emerald-50/20 border border-amber-300/80 rounded-2xl p-4 sm:p-5 shadow-xs space-y-3.5 transition-all">
                                          {/* Collapsible Header Bar */}
                                          <button
                                            type="button"
                                            id={`guidance-header-${guidanceKey}`}
                                            aria-expanded={isGuidanceExpanded}
                                            aria-controls={`guidance-body-${guidanceKey}`}
                                            aria-label={isGuidanceExpanded ? (isHi ? 'शास्त्रीय कर्म भेद मार्गदर्शन बंद करें' : 'Collapse Shastric action guidance') : (isHi ? 'शास्त्रीय कर्म भेद मार्गदर्शन देखें' : 'View Shastric action guidance')}
                                            onClick={() => toggleGuidance(guidanceKey)}
                                            className="w-full flex flex-wrap items-center justify-between gap-3 text-left cursor-pointer group"
                                          >
                                            <div className="flex items-center gap-2.5 min-w-0">
                                              <div className="w-8 h-8 rounded-xl bg-amber-500 text-white flex items-center justify-center font-bold shadow-2xs group-hover:scale-105 transition-transform shrink-0">
                                                <Scale className="w-4 h-4" />
                                              </div>
                                              <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                  <h4 className="text-xs font-bold text-astro-primary group-hover:text-astro-accent transition-colors flex items-center gap-1.5">
                                                    <span>{isHi ? 'शास्त्रीय कर्म भेद विश्लेषण (लग्न बनाम चंद्र बल)' : 'Shastric Action Guidance (Lagna vs. Chandra Bala)'}</span>
                                                  </h4>
                                                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-900 border border-amber-300">
                                                    {isHi ? 'कार्य-प्रकृति अनुसार निर्णय' : 'Purpose-Tailored Verdict'}
                                                  </span>
                                                </div>
                                                <p className="text-[10px] text-astro-secondary-text mt-0.5 line-clamp-1">
                                                  {isHi
                                                    ? 'लग्न बल स्थायी भौतिक अस्तित्व (कार्य-शरीर) बनाता है, चंद्र बल तात्कालिक जन-प्रतिक्रिया व सहजता तय करता है।'
                                                    : 'Lagna Bala builds permanent structural longevity (Karya-Sharira); Chandra Bala governs transient public friction.'}
                                                </p>
                                              </div>
                                            </div>

                                            <div className="flex items-center gap-2.5 self-end sm:self-center shrink-0">
                                              {/* Quick Score Chips */}
                                              <div className="hidden lg:flex items-center gap-1.5 font-mono text-[10px]">
                                                <span className="px-2 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold">
                                                  Lagna: {lagnaPct}%
                                                </span>
                                                <span className="px-2 py-0.5 rounded bg-indigo-50 border border-indigo-200 text-indigo-800 font-bold">
                                                  Tara: {taraPct}%
                                                </span>
                                                <span className={`px-2 py-0.5 rounded border font-bold ${
                                                  isAshtamaChandra 
                                                    ? 'bg-rose-50 border-rose-300 text-rose-800 animate-pulse' 
                                                    : chandraPct >= 70 
                                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800' 
                                                    : 'bg-amber-50 border-amber-300 text-amber-800'
                                                }`}>
                                                  Chandra (H{transitHouseFromJanma}): {chandraPct}%
                                                </span>
                                              </div>

                                              {/* Quick Status Pill */}
                                              <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border ${
                                                isAshtamaChandra 
                                                  ? 'bg-rose-100 text-rose-900 border-rose-300'
                                                  : isBedrockStrong 
                                                  ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                                                  : 'bg-amber-100 text-amber-900 border-amber-300'
                                              }`}>
                                                {isAshtamaChandra ? (isHi ? 'अष्टम चंद्र' : 'Chandrastama') : isBedrockStrong ? (isHi ? 'पंजीकरण उत्तम' : 'Asset Ready') : (isHi ? 'सावधानी' : 'Care')}
                                              </span>

                                              {/* Expand / Collapse Button Indicator */}
                                              <div className="flex items-center gap-1 text-[11px] font-bold text-astro-accent bg-white/90 hover:bg-white px-2.5 py-1 rounded-lg border border-amber-200 shadow-2xs transition-all">
                                                <span>{isGuidanceExpanded ? (isHi ? 'संक्षेप करें' : 'Collapse') : (isHi ? 'मार्गदर्शन देखें' : 'View Guidance')}</span>
                                                {isGuidanceExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                              </div>
                                            </div>
                                          </button>

                                          {/* Collapsible Guidance Content */}
                                          {isGuidanceExpanded && (
                                            <div
                                              id={`guidance-body-${guidanceKey}`}
                                              role="region"
                                              aria-labelledby={`guidance-header-${guidanceKey}`}
                                              className="space-y-3.5 pt-3 border-t border-amber-200/60 animate-in fade-in slide-in-from-top-1 duration-200"
                                            >
                                              {/* Two-Track Action Grid */}
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {/* Track 1: Structural Bedrock & Asset Securing */}
                                                <div className={`p-3.5 rounded-xl border space-y-2 transition-all ${
                                                  isBedrockStrong 
                                                    ? 'bg-emerald-50/50 border-emerald-300/80 shadow-2xs' 
                                                    : 'bg-amber-50/50 border-amber-300/80'
                                                }`}>
                                                  <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-1.5 text-xs font-bold text-emerald-950">
                                                      <Building2 className="w-4 h-4 text-emerald-700 shrink-0" />
                                                      <span>{isHi ? '१. वैधानिक पंजीकरण व स्थायी संपत्ति' : 'Track 1: Foundation & Asset Securing'}</span>
                                                    </div>
                                                    <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border shrink-0 ${
                                                      isBedrockStrong 
                                                        ? 'bg-emerald-600 text-white border-emerald-700' 
                                                        : 'bg-amber-100 text-amber-900 border-amber-300'
                                                    }`}>
                                                      {isBedrockStrong ? (isHi ? '✓ पूर्णतः उपयुक्त' : '✓ Highly Auspicious') : (isHi ? '⚠️ सावधानी रखें' : '⚠️ Proceed with Care')}
                                                    </span>
                                                  </div>

                                                  <div className="text-[11px] text-astro-secondary-text space-y-1 leading-relaxed">
                                                    <p className="font-semibold text-astro-primary">
                                                      {isHi ? 'लागू क्षेत्र:' : 'Scope:'} {isHi ? 'कंपनी निबंधन, विलेख हस्ताक्षर, अचल संपत्ति क्रय, कोर अनुबंध व स्मार्ट कॉन्ट्रैक्ट स्थापन' : 'Legal Incorporation, Deed Registration, Property Purchase, Core Filing & Foundational Contracts.'}
                                                    </p>
                                                    <p>
                                                      {isBedrockStrong
                                                        ? (isHi
                                                            ? `स्थिर लग्न (${item.lagnaDetails?.sign || ''}) एवं शुभ तारा फल स्थायी अस्तित्व को सुदृढ़ करते हैं। क्षणिक चंद्र गोचर संपत्ति के वैधानिक व भौतिक स्थायित्व को प्रभावित नहीं करता।`
                                                            : `Strong ${isSthira ? 'Sthira (Fixed) ' : ''}Lagna (${item.lagnaDetails?.sign || ''}) and favorable Tara Bala permanently anchor the structural vessel. Transient lunar transit does not erode legal asset longevity.`)
                                                        : (isHi
                                                            ? '८वें भाव अथवा लग्न शुद्धि में कमी के कारण वैधानिक दस्तावेजों की पुनः जांच आवश्यक है।'
                                                            : 'Check 8th house clean-up and document fine print prior to locking the permanent vessel.')}
                                                    </p>
                                                  </div>
                                                </div>

                                                {/* Track 2: Public Rollout, Outreach & Marketing Debut */}
                                                <div className={`p-3.5 rounded-xl border space-y-2 transition-all ${
                                                  isAshtamaChandra
                                                    ? 'bg-rose-50/60 border-rose-300 shadow-2xs'
                                                    : !isDifficultChandra && chandraPct >= 70
                                                    ? 'bg-emerald-50/50 border-emerald-300/80 shadow-2xs'
                                                    : 'bg-amber-50/60 border-amber-300'
                                                }`}>
                                                  <div className="flex items-start justify-between gap-2">
                                                    <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary">
                                                      <Megaphone className="w-4 h-4 text-amber-700 shrink-0" />
                                                      <span>{isHi ? '२. सार्वजनिक विमोचन व बाह्य संपर्क' : 'Track 2: Public Debut & Outreach'}</span>
                                                    </div>
                                                    <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-md border shrink-0 ${
                                                      isAshtamaChandra
                                                        ? 'bg-rose-600 text-white border-rose-700 animate-pulse'
                                                        : !isDifficultChandra && chandraPct >= 70
                                                        ? 'bg-emerald-600 text-white border-emerald-700'
                                                        : 'bg-amber-100 text-amber-900 border-amber-300'
                                                    }`}>
                                                      {isAshtamaChandra 
                                                        ? (isHi ? '🛑 स्थगित रखें (अष्टम चंद्र)' : '🛑 Hold (Ashtama Chandra)') 
                                                        : !isDifficultChandra && chandraPct >= 70
                                                        ? (isHi ? '🚀 विमोचन हेतु उत्तम' : '🚀 Optimal for Launch')
                                                        : (isHi ? '⏳ 48 घंटे प्रतीक्षा अनुशंसित' : '⏳ 48h Buffer Recommended')}
                                                    </span>
                                                  </div>

                                                  <div className="text-[11px] text-astro-secondary-text space-y-1 leading-relaxed">
                                                    <p className="font-semibold text-astro-primary">
                                                      {isHi ? 'लागू क्षेत्र:' : 'Scope:'} {isHi ? 'उत्पाद लॉन्च, विज्ञापन अभियान, निवेशक प्रस्तुति, उद्घाटन समारोह व ग्राहक संपर्क' : 'Product Go-Live, Ad Campaigns, Investor Pitch, Grand Opening & User Outreach.'}
                                                    </p>
                                                    <p>
                                                      {isAshtamaChandra ? (
                                                        <span className="text-rose-900 font-semibold">
                                                          {isHi
                                                            ? `जन्म चंद्र से ८वां गोचर (अष्टम चंद्र) अचानक संवाद बाधा, सर्वर/भुगतान त्रुटि व भ्रांति पैदा कर सकता है। सार्वजनिक लॉन्च को २-३ दिन टालें।`
                                                            : `Moon in 8th from Natal Moon (Chandrastama). High vulnerability to communication blunders, checkout hiccups, or negative customer sentiment. Defer public debut 2-3 days.`}
                                                        </span>
                                                      ) : isDifficultChandra ? (
                                                        <span>
                                                          {isHi
                                                            ? `चंद्रमा जन्म चंद्र से ${transitHouseFromJanma}वें भाव में होने से प्रारंभिक परिचालन घर्षण या पूछताछ का दबाव रह सकता है। संपत्ति पंजीकरण आज करें, किन्तु सार्वजनिक प्रचार चंद्र के ${nextRashiName} राशि में प्रवेश तक रोकें।`
                                                            : `Transit Moon in ${transitHouseFromJanma}th from natal Moon brings minor logistical friction or customer support load. Secure the asset today, but roll out public marketing when Moon enters ${nextRashiName}.`}
                                                        </span>
                                                      ) : (
                                                        <span className="text-emerald-900 font-semibold">
                                                          {isHi
                                                            ? `उत्कृष्ट चंद्र बल मानसिक स्थिरता, अनुकूल संचार व सकारात्मक जन-सहानुभूति के लिए शास्त्रीय रूप से सहायक माना जाता है।`
                                                            : `Clean Chandra Bala is traditionally considered supportive for emotional steadiness and interpersonal receptivity.`}
                                                        </span>
                                                      )}
                                                    </p>
                                                  </div>
                                                </div>
                                              </div>

                                              {/* Parihara & Compensatory Shield */}
                                              {isMoonInKendraOrTrikona && (
                                                <div className="flex items-start gap-2 p-2.5 rounded-xl bg-amber-100/50 border border-amber-300/70 text-[11px] text-amber-950">
                                                  <Shield className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                                                  <div>
                                                    <span className="font-bold">{isHi ? 'मुहूर्त लग्न परिहार ढाल (Compensatory Shield):' : 'Muhurta Chart Parihara Shield:'} </span>
                                                    <span>
                                                      {isHi
                                                        ? `मुहूर्त कुंडली के ${moonHouseInMuhurta}वें भाव (केंद्र/त्रिकोण/लाभ) में चंद्रमा की स्थिति अंतर्निहित खगोलीय बल प्रदान करती है, जो व्यक्तिगत गोचर की बाधाओं को स्वतः शमित करती है।`
                                                        : `Moon is positioned in the auspicious ${moonHouseInMuhurta}th House (${moonHouseInMuhurta === 1 || moonHouseInMuhurta === 4 || moonHouseInMuhurta === 7 || moonHouseInMuhurta === 10 ? 'Kendra' : moonHouseInMuhurta === 5 || moonHouseInMuhurta === 9 ? 'Trikona' : 'Labha'}) of the Muhurta Chart itself, substantially cushioning transient friction.`}
                                                    </span>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()}
                                    {/* Vedic Lagna & House Shuddhi Analysis Card */}
                                    {item.lagnaDetails && (
                                      <div className="bg-white border border-astro-border rounded-xl p-5 shadow-xs space-y-3.5">
                                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border/60 pb-3">
                                          <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center justify-center font-bold">
                                              <Building2 className="w-4 h-4" />
                                            </div>
                                            <div>
                                              <h4 className="text-xs font-bold text-astro-primary flex items-center gap-2">
                                                <span>{isHi ? 'वैदिक लग्न शुद्धि एवं भाव विचार' : 'Vedic Lagna & House Shuddhi Analysis'}</span>
                                                <span className={`text-[10px] font-extrabold px-2 py-0.5 rounded-full border ${
                                                  item.lagnaDetails.modality === 'Sthira'
                                                    ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                                                    : item.lagnaDetails.modality === 'Chara'
                                                    ? 'bg-blue-50 text-blue-800 border-blue-200'
                                                    : 'bg-amber-50 text-amber-800 border-amber-200'
                                                }`}>
                                                  {item.lagnaDetails.modalityLabel}
                                                </span>
                                              </h4>
                                              <p className="text-[10px] text-astro-secondary-text mt-0.5">
                                                {isHi ? 'स्थिर प्रतिष्ठा, अष्टम शुद्धि एवं उपचय/केंद्र ग्रह बल' : 'Ascendant permanence, 8H Ashtama Shuddhi & Upachaya/Kendra occupancies'}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-4 text-right">
                                            {item.lagnaDetails.scoreBreakdown && (
                                              <div className="text-right bg-emerald-50/60 border border-emerald-200/80 px-2.5 py-1 rounded-lg">
                                                <span className="text-[9px] font-bold text-emerald-800 uppercase tracking-wider block">
                                                  {isHi ? 'लग्न व भाव शुद्धि अंक' : 'Lagna & Shuddhi Score'}
                                                </span>
                                                <span className="text-xs font-bold text-emerald-900 font-mono">
                                                  {item.lagnaDetails.scoreBreakdown.totalScore} / {item.lagnaDetails.scoreBreakdown.maxScore} pts
                                                  <span className="text-[10px] font-semibold text-emerald-700 ml-1">
                                                    ({Math.round((item.lagnaDetails.scoreBreakdown.totalScore / item.lagnaDetails.scoreBreakdown.maxScore) * 100)}%)
                                                  </span>
                                                </span>
                                              </div>
                                            )}
                                            <div className="text-right">
                                              <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider block">
                                                Rising Ascendant
                                              </span>
                                              <span className="text-sm font-bold text-astro-primary font-serif">
                                                {item.lagnaDetails.sign} {item.lagnaDetails.formattedDegree || `${item.lagnaDetails.degreeInSign.toFixed(1)}°`}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                          {/* Modality Explanation */}
                                          <div className="p-3 rounded-xl bg-astro-card/40 border border-astro-border/50 space-y-1.5">
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                                                <Compass className="w-3 h-3 text-astro-accent" />
                                                {isHi ? 'लग्न प्रकृति (Modality)' : 'Lagna Modality Impact'}
                                              </span>
                                              {item.lagnaDetails.scoreBreakdown && (
                                                <span className="text-[10px] font-bold font-mono text-astro-primary bg-white border border-astro-border/60 px-1.5 py-0.2 rounded">
                                                  {item.lagnaDetails.scoreBreakdown.modalityScore}/{item.lagnaDetails.scoreBreakdown.maxModalityScore} pts
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[11px] text-astro-text font-medium leading-relaxed">
                                              {item.lagnaDetails.modality === 'Sthira'
                                                ? (isHi 
                                                    ? 'स्थिर लग्न (वृष, सिंह, वृश्चिक, कुंभ) स्थायी व्यापार, भूमि, प्रतिष्ठान व दीर्घायु के लिए शास्त्रीय रूप से सर्वोत्तम है।'
                                                    : 'Sthira (Fixed) Lagna provides unshakable foundations, long-term tenure, and capital durability for enterprise.')
                                                : item.lagnaDetails.modality === 'Chara'
                                                ? (isHi
                                                    ? 'चर लग्न (मेष, कर्क, तुला, मकर) त्वरित माल निकासी, परिवहन, लॉजिस्टिक्स व चलायमान व्यापार के लिए उत्तम है।'
                                                    : 'Chara (Movable) Lagna delivers rapid asset turnaround, logistics fluidity, and quick commercial expansion.')
                                                : (isHi
                                                    ? 'द्विस्वभाव लग्न (मिथुन, कन्या, धनु, मीन) बौद्धिक, परामर्श व बहुआयामी सेवाओं के लिए अनुकूल है।'
                                                    : 'Dwisvabhava (Dual) Lagna favors intellectual consulting, trade versatility, and multi-service ventures.')}
                                            </p>
                                          </div>

                                          {/* Ashtama Shuddhi 8H */}
                                          <div className={`p-3 rounded-xl border space-y-1.5 ${
                                            item.lagnaDetails.isAshtamaClean 
                                              ? 'bg-emerald-50/50 border-emerald-200' 
                                              : 'bg-red-50/50 border-red-200'
                                          }`}>
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                                                <Shield className={`w-3 h-3 ${item.lagnaDetails.isAshtamaClean ? 'text-emerald-700' : 'text-red-700'}`} />
                                                <span className={item.lagnaDetails.isAshtamaClean ? 'text-emerald-800' : 'text-red-800'}>
                                                  {isHi ? 'अष्टम शुद्धि (8H Cleanliness)' : 'Ashtama Shuddhi (8th House)'}
                                                </span>
                                              </span>
                                              {item.lagnaDetails.scoreBreakdown && (
                                                <span className={`text-[10px] font-bold font-mono px-1.5 py-0.2 rounded border ${
                                                  item.lagnaDetails.isAshtamaClean 
                                                    ? 'bg-emerald-100 text-emerald-900 border-emerald-300' 
                                                    : 'bg-red-100 text-red-900 border-red-300'
                                                }`}>
                                                  {item.lagnaDetails.scoreBreakdown.ashtamaScore}/{item.lagnaDetails.scoreBreakdown.maxAshtamaScore} pts
                                                </span>
                                              )}
                                            </div>
                                            <p className={`text-[11px] font-medium leading-relaxed ${item.lagnaDetails.isAshtamaClean ? 'text-emerald-900' : 'text-red-900'}`}>
                                              {item.lagnaDetails.isAshtamaClean
                                                ? (isHi 
                                                    ? '✓ अष्टम भाव सर्वथा रिक्त है। कोई अशुभ ग्रह नहीं है — अचानक कर्ज़, दिवालियापन व अप्रत्याशित संकट से रक्षा।' 
                                                    : '✓ 8th House is completely vacant. Shielded against unforeseen bankruptcy, hidden liabilities, and catastrophic shocks.')
                                                : (isHi
                                                    ? `⚠️ अष्टम भाव में ग्रह उपस्थिति: ${item.lagnaDetails.ashtamaPlanets.join(', ')}। सावधानी रखें।`
                                                    : `⚠️ 8th House occupied by: ${item.lagnaDetails.ashtamaPlanets.join(', ')}. Caution recommended.`)}
                                            </p>
                                          </div>

                                          {/* Upachaya & Kendra Planetary Dispositions */}
                                          <div className="p-3 rounded-xl bg-astro-card/40 border border-astro-border/50 space-y-1.5">
                                            <div className="flex items-center justify-between">
                                              <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider flex items-center gap-1">
                                                <Award className="w-3 h-3 text-amber-600" />
                                                {isHi ? 'उपचय व केंद्र ग्रह बल' : 'Upachaya & Kendra Dispositions'}
                                              </span>
                                              {item.lagnaDetails.scoreBreakdown && (
                                                <span className="text-[10px] font-bold font-mono text-amber-900 bg-amber-100/70 border border-amber-300 px-1.5 py-0.2 rounded">
                                                  {item.lagnaDetails.scoreBreakdown.upachayaScore + item.lagnaDetails.scoreBreakdown.kendraScore}/{item.lagnaDetails.scoreBreakdown.maxUpachayaScore + item.lagnaDetails.scoreBreakdown.maxKendraScore} pts
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-[11px] text-astro-text font-medium space-y-1">
                                              <div>
                                                <strong>Upachaya (3, 6, 11H):</strong>{' '}
                                                {item.lagnaDetails.upachayaMalefics.length > 0 
                                                  ? item.lagnaDetails.upachayaMalefics.join(', ') 
                                                  : (isHi ? 'कोई पाप ग्रह नहीं' : 'Neutral')}
                                              </div>
                                              <div>
                                                <strong>Kendras (1, 4, 7, 10H):</strong>{' '}
                                                {item.lagnaDetails.kendraBenefics.length > 0 
                                                  ? item.lagnaDetails.kendraBenefics.join(', ') 
                                                  : (isHi ? 'कोई शुभ ग्रह नहीं' : 'Neutral')}
                                              </div>
                                            </div>
                                          </div>
                                        </div>

                                        {/* Special Yogas Banner if present */}
                                        {item.specialYogas && item.specialYogas.length > 0 && (
                                          <div className="p-3 rounded-xl bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-300 flex items-start gap-2.5">
                                            <Sparkles className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                            <div className="space-y-0.5">
                                              <div className="text-xs font-bold text-amber-950 flex items-center gap-2">
                                                <span>🌟 {item.specialYogas.map(y => `${y.name} (${y.hiName})`).join(' • ')}</span>
                                                <span className="text-[9px] font-extrabold px-1.5 py-0.2 rounded bg-amber-200/70 text-amber-900">
                                                  +{item.specialYogas.reduce((acc, y) => acc + (y.bonusScore || 10), 0)} Pts Bonus
                                                </span>
                                              </div>
                                              <p className="text-[11px] text-amber-900 leading-relaxed">
                                                {item.specialYogas.map(y => y.description).join(' ')}
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    )}

                                    {/* Tara Bala & Reasons Row */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="bg-white border border-astro-border rounded-xl p-5 relative overflow-hidden shadow-xs">
                                        <div className="absolute -right-6 -top-6 opacity-5">
                                          <SwastikLogo size="lg" />
                                        </div>
                                        <h4 className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-widest mb-3 flex items-center gap-1.5">
                                          <Sparkles className="w-3.5 h-3.5 text-astro-accent" /> Personalized Tara Bala
                                        </h4>
                                        <div className="flex items-baseline gap-2.5 mb-2 relative z-10">
                                          <div className="text-3xl font-bold text-astro-primary font-serif leading-none">
                                            {item.taraBala.taraName}
                                          </div>
                                          <div className={`text-xs font-bold ${getTaraColor(item.taraBala.suitability)}`}>
                                            ({item.taraBala.suitability})
                                          </div>
                                        </div>
                                        <p className="text-xs text-astro-text font-semibold mb-3 leading-relaxed relative z-10">
                                          {item.taraBala.meaning}
                                        </p>
                                        <div className="text-[10px] text-astro-primary font-bold bg-astro-primary/5 px-2.5 py-1 rounded-md border border-astro-primary/10 inline-flex items-center gap-1.5">
                                          <div className="w-1.5 h-1.5 rounded-full bg-astro-primary animate-pulse" />
                                          {mode === 'personalized' ? 'Calculated for all partners' : 'General baseline alignment'}
                                        </div>
                                      </div>

                                      <div className="bg-white border border-astro-border rounded-xl p-5 shadow-xs">
                                        <h4 className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-widest mb-3 flex items-center gap-1.5">
                                          <Info className="w-3.5 h-3.5 text-astro-primary" /> Rating Factors
                                        </h4>
                                        <div className="space-y-2.5">
                                          {item.score.reasons.slice(0, 4).map((r, i) => (
                                            <div key={i} className="flex items-start gap-2.5 text-xs text-emerald-800 font-bold">
                                              <div className="mt-0.5 w-3.5 h-3.5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                                                <CheckCircle2 className="w-2.5 h-2.5 text-emerald-600" />
                                              </div>
                                              <span>{r}</span>
                                            </div>
                                          ))}
                                          {item.score.cautions.slice(0, 3).map((c, i) => (
                                            <div key={i} className="flex items-start gap-2.5 text-xs text-astro-secondary font-bold">
                                              <div className="mt-0.5 w-3.5 h-3.5 rounded-full bg-astro-secondary/15 flex items-center justify-center shrink-0">
                                                <AlertTriangle className="w-2.5 h-2.5 text-amber-600" />
                                              </div>
                                              <span>{c}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Panchanga Grid */}
                                    <div className="space-y-3">
                                      <div className="flex items-center gap-3">
                                        <div className="h-px bg-astro-border flex-1" />
                                        <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-[0.25em]">
                                          {isHi ? 'पंचांग एवं राशि विवरण' : 'PANCHANGA & RASHI ALIGNMENT'}
                                        </span>
                                        <div className="h-px bg-astro-border flex-1" />
                                      </div>
                                      
                                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
                                        {[
                                          { label: 'Tithi', value: item.panchang.tithi, icon: Globe },
                                          { label: 'Nakshatra', value: `${item.panchang.nakshatra} (P${item.panchang.nakshatraPada})`, icon: Sparkles },
                                          { label: 'Moon Rashi (Drik)', value: `${item.panchang.moonRashi}`, icon: Compass },
                                          { label: 'Yoga', value: item.panchang.yoga, icon: Activity },
                                          { label: 'Karana', value: item.panchang.karana, icon: ShieldCheck }
                                        ].map((pItem, i) => (
                                          <div key={i} className="bg-white border border-astro-border p-3 rounded-xl text-center shadow-2xs">
                                            <span className="text-[9px] uppercase font-bold text-astro-secondary-text block mb-1 opacity-70 tracking-wider truncate">{pItem.label}</span>
                                            <span className="text-xs font-bold text-astro-primary block truncate" title={pItem.value}>{pItem.value}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Astrological Alignment: Why This Window Suits the Activity */}
                                    {(() => {
                                      const rationale = generateMuhurtaRationale(
                                        {
                                          id: selectedMilestone,
                                          label: (isHi && activeMilestone?.hiLabel) ? activeMilestone.hiLabel : (activeMilestone?.label || selectedMilestone),
                                          category: selectedCategory,
                                          subCategory: selectedSubCategory
                                        },
                                        {
                                          nakshatra: item.panchang.nakshatra,
                                          nakshatraPada: item.panchang.nakshatraPada,
                                          nakshatraType: item.panchang.nakshatraType,
                                          moonRashi: item.panchang.moonRashi,
                                          tithi: item.panchang.tithi,
                                          tithiNumber: item.panchang.tithiNumber,
                                          yoga: item.panchang.yoga,
                                          karana: item.panchang.karana,
                                          hora: item.panchang.hora
                                        },
                                        item.taraBala ? {
                                          taraName: item.taraBala.taraName,
                                          suitability: item.taraBala.suitability,
                                          meaning: item.taraBala.meaning
                                        } : undefined,
                                        isHi
                                      );

                                      return (
                                        <div className="bg-gradient-to-br from-amber-50/50 via-white to-orange-50/30 border border-amber-200/80 rounded-xl p-4 shadow-2xs space-y-3">
                                          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-100 pb-2.5">
                                            <div className="flex items-center gap-2">
                                              <div className="w-6 h-6 rounded-lg bg-astro-primary/10 text-astro-primary flex items-center justify-center font-bold text-xs">
                                                ✨
                                              </div>
                                              <div>
                                                <span className="text-xs font-bold text-astro-primary font-serif">
                                                  Why this time suits: {activeMilestone?.label || selectedMilestone}
                                                </span>
                                                <span className="text-[10px] text-astro-secondary-text block">
                                                  {item.panchang.nakshatra} Nakshatra ({item.panchang.nakshatraType?.category || 'Vedic'}) &bull; Moon in {item.panchang.moonRashi}
                                                </span>
                                              </div>
                                            </div>
                                            <span className="text-[10px] font-bold text-astro-accent bg-amber-100/70 border border-amber-200 px-2.5 py-0.5 rounded-full">
                                              Classical Muhurta Alignment
                                            </span>
                                          </div>

                                          {/* Specific Astrological Explanations for Purpose */}
                                          <div className="space-y-2 text-xs">
                                            <div className="bg-white/80 border border-amber-200/60 rounded-lg p-3 space-y-2">
                                              <div className="flex items-start gap-2">
                                                <Sparkles className="w-3.5 h-3.5 text-astro-accent shrink-0 mt-0.5" />
                                                <p className="text-xs text-astro-text font-medium leading-relaxed">
                                                  <strong className="text-astro-primary">{isHi ? 'नक्षत्र एवं कार्य अनुकूलता:' : 'Nakshatra & Domain Resonance:'}</strong> {rationale.domainResonance}
                                                </p>
                                              </div>

                                              <div className="flex items-start gap-2">
                                                <Compass className="w-3.5 h-3.5 text-astro-accent shrink-0 mt-0.5" />
                                                <p className="text-xs text-astro-text font-medium leading-relaxed">
                                                  <strong className="text-astro-primary">{isHi ? 'राशि एवं ग्रहीय संबल (दृक संगति):' : 'Rashi & Planetary Strength (Drik Alignment):'}</strong> {rationale.planetaryResonance}
                                                </p>
                                              </div>

                                              {rationale.taraResonance && (
                                                <div className="flex items-start justify-between gap-2">
                                                  <div className="flex items-start gap-2 flex-1">
                                                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                                    <p className="text-xs text-astro-text font-medium leading-relaxed">
                                                      <strong className="text-emerald-800">{isHi ? 'वैयक्तिक तारा बल:' : 'Personalized Tara Bala:'}</strong> {rationale.taraResonance}
                                                    </p>
                                                  </div>
                                                  {item.score.pillars?.taraBala && (
                                                    <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-emerald-100/90 text-emerald-900 border border-emerald-300 shrink-0">
                                                      {item.score.pillars.taraBala.score}/{item.score.pillars.taraBala.max} pts ({item.score.pillars.taraBala.percentage}%)
                                                    </span>
                                                  )}
                                                </div>
                                              )}

                                              <div className="flex items-start gap-2 pt-1 border-t border-amber-100/70">
                                                <Globe className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
                                                <p className="text-xs text-astro-text font-medium leading-relaxed">
                                                  <strong className="text-astro-primary">{isHi ? 'पंचांग संतुलन:' : 'Panchanga Harmony:'}</strong> {rationale.panchangaHarmony}
                                                </p>
                                              </div>
                                            </div>

                                            {item.panchang.nakshatraType && (
                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 pt-1">
                                                <div className="bg-white/60 border border-amber-200/50 rounded-lg p-2.5 space-y-1">
                                                  <div className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider">
                                                    Nakshatra Nature ({item.panchang.nakshatraType.sanskritType})
                                                  </div>
                                                  <p className="text-[11px] text-astro-text font-medium leading-normal">
                                                    {item.panchang.nakshatraType.description}
                                                  </p>
                                                </div>
                                                <div className="bg-white/60 border border-amber-200/50 rounded-lg p-2.5 space-y-1">
                                                  <div className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-wider">
                                                    Classical Subha Karya (Ideal Actions)
                                                  </div>
                                                  <div className="flex flex-wrap gap-1">
                                                    {item.panchang.nakshatraType.suitableFor.map((act, aIdx) => (
                                                      <span 
                                                        key={aIdx} 
                                                        className="text-[10px] font-semibold bg-white border border-amber-200/70 text-astro-primary px-1.5 py-0.5 rounded shadow-2xs"
                                                      >
                                                        ✓ {act}
                                                      </span>
                                                    ))}
                                                  </div>
                                                </div>
                                              </div>
                                            )}
                                          </div>

                                          <div className="bg-emerald-50/50 rounded-lg p-2.5 text-[11px] text-astro-text font-medium flex items-start gap-2 border border-emerald-200/60">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0 mt-0.5" />
                                            <div className="leading-snug">
                                              <strong>{isHi ? 'शुद्ध एवं निर्दोष काल:' : 'Filtered & Pure:'}</strong> {rationale.safetyShield}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })()}

                                    {/* Multi-Day Daily Slots Breakdown */}
                                    {item.dailySlots && item.dailySlots.length > 0 ? (
                                      <div className="space-y-3 pt-1">
                                        <div className="flex items-center gap-3">
                                          <div className="h-px bg-astro-border flex-1" />
                                          <span className="text-[10px] font-bold text-astro-secondary-text uppercase tracking-[0.25em]">
                                            {isHi ? 'दैनिक मुहूर्त एवं चौघड़िया विवरण' : 'DAILY MUHURTA & CHOGHADIYA WINDOWS'}
                                          </span>
                                          <div className="h-px bg-astro-border flex-1" />
                                        </div>

                                        <div className="space-y-3">
                                          {item.dailySlots.map((slot, sIdx) => (
                                            <div key={sIdx} className="bg-white border border-astro-border rounded-xl p-4 shadow-2xs space-y-3">
                                              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-astro-border pb-2">
                                                <div className="flex items-center gap-2">
                                                  <Calendar className="w-4 h-4 text-astro-primary" />
                                                  <span className="text-xs font-bold text-astro-primary font-serif">
                                                    {slot.formattedDate}
                                                  </span>
                                                </div>
                                                {slot.validityNote && (
                                                  <span className="text-[10px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                                                    ✨ {slot.validityNote}
                                                  </span>
                                                )}
                                              </div>

                                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                                {/* Highly Favorable Slots */}
                                                <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-3 space-y-2">
                                                  <div className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider flex items-center gap-1.5">
                                                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> Favorable Slots
                                                  </div>
                                                  <div className="space-y-1.5">
                                                    <div className="flex flex-col text-xs space-y-0.5">
                                                      <div className="flex justify-between items-center">
                                                        <span className="font-semibold text-emerald-950">Abhijit Muhurta:</span>
                                                        <span className="font-bold text-emerald-800">
                                                          {formatSmartDateTime(slot.abhijit.rawStart, false)} - {formatSmartDateTime(slot.abhijit.rawEnd, false)}
                                                        </span>
                                                      </div>
                                                      {slot.abhijit.conflict && (
                                                        <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1 font-semibold">
                                                          ⚠️ Overlaps with {slot.abhijit.conflict} (Caution)
                                                        </div>
                                                      )}
                                                    </div>
                                                    {slot.favorableChoghadiyas && slot.favorableChoghadiyas.length > 0 && (
                                                      <div className="pt-1.5 border-t border-emerald-100">
                                                        <span className="text-[10px] text-emerald-900 font-bold block mb-1">Auspicious Choghadiyas (Day):</span>
                                                        <div className="space-y-1">
                                                          {slot.favorableChoghadiyas.map((ch, cIdx) => (
                                                            <div key={cIdx} className="flex flex-col text-[11px] py-1 border-b border-emerald-100 last:border-0 space-y-0.5">
                                                              <div className="flex justify-between items-center">
                                                                <span className="text-emerald-900 font-medium">{ch.name} {ch.hiName ? `(${ch.hiName})` : ''}:</span>
                                                                <span className="font-semibold text-emerald-800">{ch.timeRange}</span>
                                                              </div>
                                                              {ch.conflict && (
                                                                <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex items-center gap-1 font-semibold">
                                                                  ⚠️ Intersects with {ch.conflict} — Avoid/Exercise Caution
                                                                </div>
                                                              )}
                                                            </div>
                                                          ))}
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>

                                                {/* Inauspicious Periods to Avoid */}
                                                <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-3 space-y-2">
                                                  <div className="text-[10px] font-bold text-rose-800 uppercase tracking-wider flex items-center gap-1.5">
                                                    <X className="w-3.5 h-3.5 text-rose-500" /> Periods to Avoid
                                                  </div>
                                                  <div className="space-y-1.5">
                                                    <div className="flex justify-between items-center text-xs">
                                                      <span className="font-semibold text-rose-950">Rahu Kalam:</span>
                                                      <span className="font-bold text-rose-800">
                                                        {formatSmartDateTime(slot.rahuKalam.rawStart, false)} - {formatSmartDateTime(slot.rahuKalam.rawEnd, false)}
                                                      </span>
                                                    </div>
                                                    <div className="flex justify-between items-center text-xs">
                                                      <span className="font-semibold text-rose-950">Yamaganda:</span>
                                                      <span className="font-bold text-rose-800">
                                                        {formatSmartDateTime(slot.yamaganda.rawStart, false)} - {formatSmartDateTime(slot.yamaganda.rawEnd, false)}
                                                      </span>
                                                    </div>
                                                    {slot.gulikaKalam && (
                                                      <div className="flex justify-between items-center text-xs">
                                                        <span className="font-semibold text-rose-950">Gulika:</span>
                                                        <span className="font-bold text-rose-800">
                                                          {formatSmartDateTime(slot.gulikaKalam.rawStart, false)} - {formatSmartDateTime(slot.gulikaKalam.rawEnd, false)}
                                                        </span>
                                                      </div>
                                                    )}
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                        <div className="bg-rose-50/40 border border-rose-100 rounded-xl p-4">
                                          <div className="flex items-center gap-1.5 mb-3">
                                            <X className="w-3.5 h-3.5 text-rose-500" />
                                            <span className="text-[10px] font-bold text-rose-800 uppercase tracking-wider">Inauspicious Periods</span>
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex justify-between items-center text-xs border-b border-rose-100 pb-1.5">
                                              <span className="font-semibold text-rose-700">Rahu Kalam</span>
                                              <span className="font-bold text-astro-primary">
                                                {formatSmartDateTime(item.panchang.rahuKalam.rawStart, false)} - {formatSmartDateTime(item.panchang.rahuKalam.rawEnd, false)}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs border-b border-rose-100 pb-1.5">
                                              <span className="font-semibold text-rose-700">Yamaganda</span>
                                              <span className="font-bold text-astro-primary">
                                                {formatSmartDateTime(item.panchang.yamaganda.rawStart, false)} - {formatSmartDateTime(item.panchang.yamaganda.rawEnd, false)}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                              <span className="font-semibold text-rose-700">Gulika</span>
                                              <span className="font-bold text-astro-primary">
                                                {formatSmartDateTime(item.panchang.gulikaKalam.rawStart, false)} - {formatSmartDateTime(item.panchang.gulikaKalam.rawEnd, false)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>

                                        <div className="bg-emerald-50/40 border border-emerald-100 rounded-xl p-4">
                                          <div className="flex items-center gap-1.5 mb-3">
                                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                                            <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider">Highly Favorable</span>
                                          </div>
                                          <div className="space-y-2">
                                            <div className="flex justify-between items-center text-xs border-b border-emerald-100 pb-1.5">
                                              <span className="font-semibold text-emerald-700">Abhijit Muhurta</span>
                                              <span className="font-bold text-astro-primary">
                                                {formatSmartDateTime(item.panchang.abhijit.rawStart, false)} - {formatSmartDateTime(item.panchang.abhijit.rawEnd, false)}
                                              </span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs border-b border-emerald-100 pb-1.5">
                                              <span className="font-semibold text-emerald-700">Active Hora</span>
                                              <span className="font-bold text-astro-primary font-serif">{item.panchang.hora} Hora</span>
                                            </div>
                                            <div className="flex justify-between items-center text-xs">
                                              <span className="font-semibold text-emerald-700">Local Sunrise</span>
                                              <span className="font-bold text-astro-primary">
                                                {formatSmartDateTime(new Date(item.panchang.sunrise), false)}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Astrological Scriptural Source */}
                                    {activeMilestone?.rules && (
                                      <div className="bg-white border border-astro-border rounded-xl p-3 text-[11px] text-astro-secondary-text shadow-2xs">
                                        <div className="font-bold text-astro-accent mb-0.5">
                                          Jyotish Reference Profile: {activeMilestone.rules.source || 'Vedic Classics'}
                                        </div>
                                        <div className="flex flex-wrap gap-4">
                                          <span><strong>Tradition:</strong> {activeMilestone.rules.tradition || 'Vedic Tradition'}</span>
                                          <span><strong>Rules Confidence:</strong> {activeMilestone.rules.confidence || 'High'}</span>
                                        </div>
                                      </div>
                                    )}

                                    {/* KUNDALI-BASED MACRO-CLIMATE ADVISORY & REASONING PANEL */}
                                    {mode === 'personalized' && (
                                      <div className="bg-gradient-to-br from-amber-50/70 via-white to-orange-50/40 border border-amber-300/80 rounded-2xl p-4 sm:p-5 shadow-xs space-y-4">
                                        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-amber-200/80 pb-3">
                                          <div className="flex items-center gap-2.5">
                                            <div className="w-8 h-8 rounded-xl bg-amber-500/10 text-amber-700 flex items-center justify-center font-bold text-sm">
                                              🛡️
                                            </div>
                                            <div>
                                              <h4 className="text-xs font-bold text-astro-primary font-serif uppercase tracking-wider">
                                                Macro-Climate Advisory & Kundali Resonance
                                              </h4>
                                              <span className="text-[10px] text-astro-secondary-text">
                                                Native Kundali Evaluation &bull; Background Dasha / Gochar vs 2-Hour Micro-Window
                                              </span>
                                            </div>
                                          </div>
                                          <span className="text-[10px] font-bold bg-amber-100 text-amber-900 border border-amber-300 px-2.5 py-0.5 rounded-full">
                                            Advisory Only (Non-Blocking)
                                          </span>
                                        </div>

                                        {/* Caution Window & Context Explanation */}
                                        <div className="space-y-3 text-xs">
                                          <div className="bg-amber-50/80 border border-amber-200 rounded-xl p-3.5 space-y-2">
                                            <div className="flex items-start gap-2">
                                              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                              <div className="space-y-1">
                                                <span className="font-bold text-amber-950 text-xs">
                                                  Astrological Caution & Suitability Notice
                                                </span>
                                                <p className="text-xs text-amber-900 leading-relaxed">
                                                  While this specific 2-hour micro-window holds strong Shuddhi (Purity) and positive Tara Bala, please note that this acts strictly as an advisory evaluation. If your background Mahadasha / Antardasha or major slow-planet transit (Gochar) is currently navigating a testing cycle for this specific life domain, the micro-window provides localized protection, but overall fruition depends on the broader planetary climate.
                                                </p>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Domain & Kundali Suitability Details */}
                                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div className="bg-white border border-astro-border rounded-xl p-3 space-y-1.5 shadow-2xs">
                                              <div className="flex items-center gap-1.5 text-astro-primary font-bold text-[11px]">
                                                <Compass className="w-3.5 h-3.5 text-astro-accent" />
                                                <span>Dik-Shuddhi & Favorable Direction</span>
                                              </div>
                                              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                                                Optimal execution direction: <strong>{selectedCategory === 'business' || selectedCategory === 'finance' ? 'North / East (Kuber & Indra Sthana)' : selectedCategory === 'property' || selectedCategory === 'construction' ? 'East / North-East (Ishanya Sthana)' : 'East / North (Deva Sthana)'}</strong>. Facing or initiating towards these quarters shall augment success.
                                              </p>
                                            </div>

                                            <div className="bg-white border border-astro-border rounded-xl p-3 space-y-1.5 shadow-2xs">
                                              <div className="flex items-center gap-1.5 text-emerald-800 font-bold text-[11px]">
                                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                                                <span>Tithi-Sandhi & Rikta Filter Check</span>
                                              </div>
                                              <p className="text-[11px] text-emerald-900 leading-relaxed">
                                                Verified clean: Zero incidence of Rikta Tithi (4, 9, 14) or Gandanta/Sandhi junctions. The lunar energy operates with wholesome stability.
                                              </p>
                                            </div>
                                          </div>

                                          {/* Multi-Partner Consensus & Primary Signatory Advice */}
                                          {selectedPartners.length > 1 && (
                                            <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-3 space-y-1.5">
                                              <div className="flex items-center justify-between">
                                                <span className="text-xs font-bold text-indigo-950 flex items-center gap-1.5">
                                                  👥 Multi-Kundali Execution Strategy
                                                </span>
                                                <span className="text-[10px] font-bold text-indigo-700 bg-white px-2 py-0.5 rounded border border-indigo-200">
                                                  {selectedPartners.length} Partners Checked
                                                </span>
                                              </div>
                                              <p className="text-[11px] text-indigo-900 leading-relaxed">
                                                Primary Signatory Recommendation: Having <strong>{selectedPartners[0].name}</strong> (or the partner holding the strongest Sampat/Sadhana Tara) initiate the principal ritual, deed signature, or payment dispatch shall maximize collective fruition.
                                              </p>
                                            </div>
                                          )}

                                          {/* User Override & Pandit Guidance Reassurance */}
                                          <div className="bg-white/80 border border-astro-border rounded-xl p-3 text-[11px] text-astro-secondary-text leading-relaxed flex items-start gap-2">
                                            <Info className="w-3.5 h-3.5 text-astro-primary shrink-0 mt-0.5" />
                                            <div>
                                              <strong>Independent Guidance Reassurance:</strong> If you are already proceeding under the personalized guidance of your family astrologer/pandit or have non-negotiable administrative deadlines, you may confidently proceed with the designated rituals; this advisory serves as supplementary Vedic awareness.
                                            </div>
                                          </div>

                                          {/* PERSONA-BASED ACTION CTAs */}
                                          <div className="pt-2 border-t border-amber-200/60 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                                            <div className="text-[11px] text-astro-secondary-text">
                                              Need a complete multi-layered reading of your Dasha, Gochar transits, and Life Themes?
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2 shrink-0">
                                              {/* Basic User CTA */}
                                              <a
                                                href="#contact-astrologer"
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  const contactBtn = document.getElementById('header-contact-btn');
                                                  if (contactBtn) {
                                                    contactBtn.click();
                                                  } else {
                                                    window.dispatchEvent(new CustomEvent('open-contact-modal'));
                                                  }
                                                }}
                                                className="px-3.5 py-2 rounded-xl bg-astro-primary text-white font-bold text-xs hover:bg-astro-primary/90 transition-all flex items-center gap-1.5 shadow-xs cursor-pointer"
                                              >
                                                <MessageSquare className="w-3.5 h-3.5" />
                                                <span>Discuss with Astrologer (Personal Consultation)</span>
                                              </a>

                                              {/* Pro / Acharya Life Domain Analysis CTA */}
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  window.dispatchEvent(new CustomEvent('switch-workbench-tab', { detail: { tab: 2, lifeDomain: selectedCategory || 'general' } }));
                                                }}
                                                className="px-3.5 py-2 rounded-xl bg-white border border-astro-border text-astro-primary font-bold text-xs hover:bg-astro-card transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                                              >
                                                <BookOpen className="w-3.5 h-3.5 text-astro-accent" />
                                                <span>Analyze Kundali in Detail for this Life Domain</span>
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* 1-CLICK CLIENT SHARING / WHATSAPP SUMMARY CARD */}
                                    <div className="bg-white border border-astro-border rounded-xl p-4 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                      <div className="space-y-0.5">
                                        <div className="text-xs font-bold text-astro-primary flex items-center gap-1.5">
                                          <span>Astrologer Client Dispatch</span>
                                          <span className="text-[10px] font-normal text-astro-secondary-text">• 1-Click Summary</span>
                                        </div>
                                        <p className="text-[11px] text-astro-secondary-text">
                                          Instantly copy or share this verified Muhurta window with client notes and Panchanga breakdown.
                                        </p>
                                      </div>

                                      <div className="flex items-center gap-2 shrink-0">
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const summaryText = `🕉️ *Vedic Muhurta Recommendation*\n` +
                                              `📌 *Purpose:* ${activeMilestone?.label || selectedMilestone}\n` +
                                              `👤 *Native:* ${mode === 'personalized' ? selectedPartners.map(p => p.name).join(', ') : 'General Query'}\n` +
                                              `📅 *Date & Time:* ${formatSmartDateTime(item.startTime)} - ${formatSmartDateTime(item.endTime)}\n` +
                                              `📍 *Location:* ${loc.name || loc.display_name}\n` +
                                              `⭐ *Panchanga:* ${item.panchang.tithi} | ${item.panchang.nakshatra} (Pada ${item.panchang.nakshatraPada}) | Moon in ${item.panchang.moonRashi}\n` +
                                              `✨ *Auspicious Score:* ${item.score.totalScore}/100\n` +
                                              `🧭 *Direction:* ${selectedCategory === 'business' ? 'North / East' : 'East / North'}\n` +
                                              `🌿 *Purity:* Zero Rahu Kalam / Yamaganda overlap. Verified Shuddhi.`;
                                            navigator.clipboard.writeText(summaryText);
                                            setCopiedSummaryIdx(rIdx);
                                            setTimeout(() => setCopiedSummaryIdx(null), 3000);
                                          }}
                                          className="px-3 py-2 rounded-xl bg-[#F9F7F3] border border-astro-border text-astro-primary font-bold text-xs hover:bg-astro-card transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
                                        >
                                          {copiedSummaryIdx === rIdx ? (
                                            <>
                                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                                              <span className="text-emerald-700">Copied!</span>
                                            </>
                                          ) : (
                                            <>
                                              <Copy className="w-3.5 h-3.5 text-astro-secondary-text" />
                                              <span>Copy Summary</span>
                                            </>
                                          )}
                                        </button>

                                        <a
                                          href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                            `🕉️ *Vedic Muhurta Recommendation*\n` +
                                            `📌 *Purpose:* ${activeMilestone?.label || selectedMilestone}\n` +
                                            `👤 *Native:* ${mode === 'personalized' ? selectedPartners.map(p => p.name).join(', ') : 'General Query'}\n` +
                                            `📅 *Date & Time:* ${formatSmartDateTime(item.startTime)} - ${formatSmartDateTime(item.endTime)}\n` +
                                            `📍 *Location:* ${loc.name || loc.display_name}\n` +
                                            `⭐ *Panchanga:* ${item.panchang.tithi} | ${item.panchang.nakshatra} | Moon in ${item.panchang.moonRashi}\n` +
                                            `✨ *Score:* ${item.score.totalScore}/100\n` +
                                            `🌿 *Purity:* Clean Shuddhi without Rahu/Yamaganda overlap.`
                                          )}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="px-3 py-2 rounded-xl bg-emerald-600 text-white font-bold text-xs hover:bg-emerald-700 transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs"
                                        >
                                          <ExternalLink className="w-3.5 h-3.5" />
                                          <span>Share on WhatsApp</span>
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                            });
                          })()}
                        </div>
                      );
                    })}
                  </div>

                  {/* ALL BETTER SCORING MUHURTAS IN NEXT 3 MONTHS (BOTTOM OF RESULT PAGE) */}
                  {isCheckingFuture && (
                    <div className="bg-amber-50/80 border border-amber-200/90 rounded-2xl p-4 flex items-center justify-center gap-3 text-xs text-amber-900 shadow-2xs animate-pulse">
                      <Sparkles className="w-4 h-4 text-amber-600 animate-spin shrink-0" />
                      <span>{isHi ? 'अगले ३ माह (९० दिन) में सभी उच्चतर अंक वाले मुहूर्तों का विश्लेषण किया जा रहा है...' : 'Scanning the next 3 months (90 days) for all higher-scoring Muhurta windows...'}</span>
                    </div>
                  )}

                  {!isCheckingFuture && betterFutureMuhurtas.length > 0 && (() => {
                    const sortedFutureList = [...betterFutureMuhurtas].sort((a, b) => compareFutureMuhurtas(a, b, sortOrder));
                    const displayedFutureList = sortedFutureList.slice(0, visibleFutureCount);
                    const topScore = Math.max(...betterFutureMuhurtas.map(b => b.muhurta.score.totalScore));
                    const maxDiff = Math.max(...betterFutureMuhurtas.map(b => b.scoreDifference));

                    return (
                      <div className="bg-gradient-to-br from-amber-50/90 via-white to-emerald-50/40 border-2 border-amber-400/90 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-sm space-y-5" id="next-3-months-better-muhurta">
                        {/* Section Header */}
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 border-b border-amber-200/60 pb-4">
                          <div className="flex items-start gap-3">
                            <div className="w-11 h-11 rounded-2xl bg-amber-500/20 text-amber-800 flex items-center justify-center font-bold text-2xl shrink-0 mt-0.5 border border-amber-300 shadow-2xs">
                              🌟
                            </div>
                            <div>
                              <div className="flex flex-wrap items-center gap-2">
                                <h4 className="text-base sm:text-lg font-bold text-astro-primary font-serif">
                                  {isHi ? 'अगले ३ माह के सभी उच्चतर अंक वाले मुहूर्त' : 'All Higher-Scoring Muhurtas in Next 3 Months'}
                                </h4>
                                <span className="text-[11px] font-extrabold bg-amber-200 text-amber-950 px-2.5 py-0.5 rounded-full border border-amber-300 flex items-center gap-1 shadow-2xs">
                                  <Award className="w-3 h-3 text-amber-900" />
                                  {isHi ? `${betterFutureMuhurtas.length} श्रेष्ठ मुहूर्त उपलब्ध` : `${betterFutureMuhurtas.length} Superior Windows Found`}
                                </span>
                                {maxDiff > 0 && (
                                  <span className="text-[11px] font-extrabold bg-emerald-100 text-emerald-900 px-2.5 py-0.5 rounded-full border border-emerald-300 flex items-center gap-1">
                                    <TrendingUp className="w-3 h-3 text-emerald-700" />
                                    {isHi ? `+${maxDiff}% तक अधिक अंक` : `Up to +${maxDiff}% Higher Score`}
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-astro-secondary-text mt-1">
                                {isHi
                                  ? 'वर्तमान खोजी गई अवधि से अधिक अनुकूल व फलदायी सभी आगामी मुहूर्त तिथियों की पूरी सूची।'
                                  : 'Comprehensive list of upcoming dates within 90 days that achieve a stronger auspicious score than your current search dates.'}
                              </p>
                            </div>
                          </div>

                          {/* Controls: Sort and Top Peak Display */}
                          <div className="flex flex-wrap items-center gap-2 self-start md:self-center">
                            <div
                              className="inline-flex bg-white/90 border border-amber-200 rounded-xl p-1 text-[11px] font-bold shadow-2xs"
                              role="group"
                              aria-label={isHi ? 'मुहूर्त क्रमबद्धता चयन' : 'Muhurta sorting selection'}
                            >
                              <button
                                type="button"
                                role="radio"
                                aria-checked={sortOrder === 'score'}
                                onClick={() => setSortOrder('score')}
                                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                                  sortOrder === 'score'
                                    ? 'bg-amber-600 text-white shadow-2xs'
                                    : 'text-astro-secondary-text hover:text-amber-800'
                                }`}
                                aria-label={isHi ? 'सभी मुहूर्त अंक अनुसार क्रमबद्ध करें (सर्वोच्च पहले)' : 'Sort all Muhurtas by score (highest first)'}
                              >
                                <Award className="w-3 h-3" />
                                <span>{isHi ? 'अंक अनुसार (सर्वोच्च)' : 'By Score (Highest)'}</span>
                              </button>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={sortOrder === 'date'}
                                onClick={() => setSortOrder('date')}
                                className={`px-2.5 py-1 rounded-lg transition-all cursor-pointer flex items-center gap-1 ${
                                  sortOrder === 'date'
                                    ? 'bg-amber-600 text-white shadow-2xs'
                                    : 'text-astro-secondary-text hover:text-amber-800'
                                }`}
                                aria-label={isHi ? 'सभी मुहूर्त तिथि अनुसार क्रमबद्ध करें (सबसे पहले वाला पहले)' : 'Sort all Muhurtas by date (earliest first)'}
                              >
                                <Calendar className="w-3 h-3" />
                                <span>{isHi ? 'तिथि अनुसार (कालक्रम)' : 'By Date (Earliest)'}</span>
                              </button>
                            </div>

                            <button
                              type="button"
                              onClick={() => setShowScoreExplanationModal(true)}
                              className="px-2.5 py-1.5 rounded-xl bg-white/90 border border-amber-200 text-astro-primary hover:text-astro-accent text-[11px] font-bold flex items-center gap-1 shadow-2xs cursor-pointer"
                              title="Why does Combined differ from Navtara/Rashi?"
                            >
                              <HelpCircle className="w-3.5 h-3.5 text-astro-accent" />
                              <span>{isHi ? 'अंक गणना सूत्र' : 'Score Math'}</span>
                            </button>

                            {isDatesModifiedFromOriginal && originalSearchDates && (
                              <button
                                type="button"
                                onClick={handleRevertToOriginalDate}
                                className="px-3 py-1.5 rounded-xl bg-amber-900 hover:bg-amber-950 text-white text-[11px] font-bold flex items-center gap-1.5 shadow-2xs cursor-pointer transition-all"
                                title={isHi ? 'मूल अनुरोधित तारीख पर वापस जाएं' : 'Return to originally requested date'}
                              >
                                <RotateCcw className="w-3.5 h-3.5 text-amber-300" />
                                <span>{isHi ? 'मूल तारीख पर वापस जाएं' : 'Back to Original Date'}</span>
                              </button>
                            )}
                          </div>
                        </div>

                        {/* List of All Superior Muhurtas */}
                        <div className="space-y-3.5">
                          {displayedFutureList.map((item) => {
                            // Stable per-window identity so expansion survives re-sorting
                            const futureId = item.muhurta.startTime || item.muhurta.dateTime || item.muhurta.evaluatedMoment;
                            const isExpanded = expandedFutureIds.has(futureId);
                            const itemDate = new Date(item.muhurta.dateTime);

                            return (
                              <div
                                key={futureId}
                                className="bg-white/95 border border-amber-200/90 hover:border-amber-400 rounded-2xl p-4 sm:p-5 shadow-2xs transition-all duration-200 space-y-3.5"
                              >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  {/* Left: Date & Time Window */}
                                  <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                      <Calendar className="w-4 h-4 text-amber-600 shrink-0" />
                                      <span className="text-sm sm:text-base font-bold text-astro-primary font-serif">
                                        {itemDate.toLocaleDateString(isHi ? 'hi-IN' : 'en-US', {
                                          weekday: 'long',
                                          day: 'numeric',
                                          month: 'long',
                                          year: 'numeric'
                                        })}
                                      </span>
                                      {item.muhurta.score.totalScore === topScore && (
                                        <span className="text-[10px] font-extrabold bg-amber-100 text-amber-900 border border-amber-300 px-2 py-0.5 rounded-full">
                                          👑 {isHi ? 'सर्वोत्तम' : 'Top Window'}
                                        </span>
                                      )}
                                    </div>

                                    <div className="flex flex-wrap items-center gap-3 text-xs text-astro-secondary-text">
                                      <div className="flex items-center gap-1 text-astro-primary font-semibold">
                                        <Clock className="w-3.5 h-3.5 text-astro-accent shrink-0" />
                                        <span>
                                          {formatSmartDateTime(item.muhurta.startTime)} — {formatSmartDateTime(item.muhurta.endTime)}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <MapPin className="w-3.5 h-3.5 text-astro-accent shrink-0" />
                                        <span>{item.locationName}</span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Right: Score Pill & Difference */}
                                  <div className="flex items-center sm:flex-col sm:items-end justify-between gap-1.5 shrink-0">
                                    <div className="flex items-baseline gap-1.5">
                                      <span className="text-xl sm:text-2xl font-black font-serif text-emerald-700">
                                        {item.muhurta.score.totalScore}%
                                      </span>
                                      <span className="text-xs font-bold text-emerald-800">
                                        ({item.muhurta.score.label})
                                      </span>
                                    </div>
                                    {item.scoreDifference > 0 && (
                                      <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                                        <TrendingUp className="w-2.5 h-2.5" />
                                        +{item.scoreDifference}% {isHi ? 'वर्तमान से अधिक' : 'higher'}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Panchang Highlights Bar */}
                                <div className="flex flex-wrap gap-1.5 pt-0.5">
                                  <span className="text-[11px] font-semibold bg-amber-500/10 text-amber-950 border border-amber-200 px-2.5 py-0.5 rounded-lg flex items-center gap-1">
                                    ⭐ <span>{item.muhurta.panchang.nakshatra}</span>
                                    <span className="text-[9px] opacity-75">(Pada {item.muhurta.panchang.nakshatraPada})</span>
                                  </span>
                                  <span className="text-[11px] font-semibold bg-emerald-500/10 text-emerald-950 border border-emerald-200 px-2.5 py-0.5 rounded-lg">
                                    🌙 {item.muhurta.panchang.tithi}
                                  </span>
                                  <span className="text-[11px] font-semibold bg-blue-500/10 text-blue-950 border border-blue-200 px-2.5 py-0.5 rounded-lg">
                                    ♈ {item.muhurta.panchang.moonRashi}
                                  </span>
                                  <span className="text-[11px] font-semibold bg-indigo-500/10 text-indigo-950 border border-indigo-200 px-2.5 py-0.5 rounded-lg">
                                    🛡️ {item.muhurta.taraBala.taraName} ({item.muhurta.taraBala.suitability})
                                  </span>
                                  {item.muhurta.panchang.hora && (
                                    <span className="text-[11px] font-semibold bg-purple-500/10 text-purple-950 border border-purple-200 px-2.5 py-0.5 rounded-lg">
                                      ☀️ {item.muhurta.panchang.hora} Hora
                                    </span>
                                  )}
                                  {item.muhurta.lagnaDetails && (
                                    <span className={`text-[11px] font-semibold border px-2.5 py-0.5 rounded-lg flex items-center gap-1 ${
                                      item.muhurta.lagnaDetails.modality === 'Sthira'
                                        ? 'bg-emerald-500/10 text-emerald-950 border-emerald-300'
                                        : item.muhurta.lagnaDetails.modality === 'Chara'
                                        ? 'bg-blue-500/10 text-blue-950 border-blue-300'
                                        : 'bg-amber-500/10 text-amber-950 border-amber-300'
                                    }`}>
                                      <Building2 className="w-3 h-3 text-amber-700 shrink-0" />
                                      <span>Lagna: {item.muhurta.lagnaDetails.sign} ({item.muhurta.lagnaDetails.modalityLabel.split(' ')[0]})</span>
                                    </span>
                                  )}
                                </div>

                                {/* Key Advantages / Reasons why it scores higher */}
                                <div className="bg-amber-50/50 border border-amber-200/60 rounded-xl p-3 space-y-1.5">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900 block flex items-center gap-1">
                                    <Sparkles className="w-3 h-3 text-amber-600" />
                                    {isHi ? 'इस मुहूर्त की प्रमुख विशिष्टताएं:' : 'Key Astrological Strengths of this Window:'}
                                  </span>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-astro-secondary-text">
                                    {item.muhurta.score.reasons.slice(0, 4).map((reason, rIdx) => (
                                      <div key={rIdx} className="flex items-start gap-1.5 leading-snug">
                                        <span className="text-emerald-600 font-bold shrink-0">✓</span>
                                        <span>{reason}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Expandable Technical Alignment Factors */}
                                {isExpanded && (
                                  <div className="bg-astro-bg/40 border border-astro-border rounded-xl p-4 space-y-3">
                                    <h5 className="text-xs font-bold text-astro-primary font-serif uppercase tracking-wider flex items-center gap-1.5">
                                      <Compass className="w-3.5 h-3.5 text-astro-accent" />
                                      {isHi ? 'विस्तृत वैदिक पंचांग एवं ग्रह संरेखण' : 'Full Vedic Panchanga & Alignment Factors'}
                                    </h5>

                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                      <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs">
                                        <span className="text-[10px] text-astro-secondary-text uppercase block font-semibold">Tithi</span>
                                        <span className="font-bold text-astro-primary">{item.muhurta.panchang.tithi}</span>
                                      </div>
                                      <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs">
                                        <span className="text-[10px] text-astro-secondary-text uppercase block font-semibold">Nakshatra</span>
                                        <span className="font-bold text-astro-primary">{item.muhurta.panchang.nakshatra} (Pada {item.muhurta.panchang.nakshatraPada})</span>
                                      </div>
                                      <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs">
                                        <span className="text-[10px] text-astro-secondary-text uppercase block font-semibold">Moon Sign</span>
                                        <span className="font-bold text-astro-primary">{item.muhurta.panchang.moonRashi}</span>
                                      </div>
                                      <div className="bg-white p-2.5 rounded-lg border border-astro-border/60 shadow-2xs">
                                        <span className="text-[10px] text-astro-secondary-text uppercase block font-semibold">Yoga & Karana</span>
                                        <span className="font-bold text-astro-primary">{item.muhurta.panchang.yoga} / {item.muhurta.panchang.karana}</span>
                                      </div>
                                    </div>

                                    {/* Score Points Anatomy Breakdown */}
                                    <div className="bg-white p-3 rounded-lg border border-astro-border/60 space-y-2">
                                      <div className="flex items-center justify-between text-xs font-bold text-astro-primary">
                                        <span>{isHi ? 'अंक संरचना (Point Breakdown)' : 'Scoring Anatomy (7 Limbs Points)'}</span>
                                        <button
                                          type="button"
                                          onClick={() => setShowScoreExplanationModal(true)}
                                          className="text-[10px] text-astro-accent hover:underline font-semibold flex items-center gap-0.5"
                                        >
                                          <HelpCircle className="w-3 h-3" />
                                          {isHi ? 'यह कैसे काम करता है?' : 'How is this scored?'}
                                        </button>
                                      </div>

                                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                                        <div className="bg-astro-card/40 p-2 rounded border border-astro-border/40">
                                          <span className="text-[10px] text-astro-secondary-text block">Tara Bala:</span>
                                          <span className="font-bold text-astro-primary">{item.muhurta.score.breakdown.taraBala} / {item.muhurta.score.maxBreakdown?.taraBala ?? 25} pts</span>
                                        </div>
                                        <div className="bg-astro-card/40 p-2 rounded border border-astro-border/40">
                                          <span className="text-[10px] text-astro-secondary-text block">Moon Transit:</span>
                                          <span className="font-bold text-astro-primary">{item.muhurta.score.breakdown.moonCondition} / {item.muhurta.score.maxBreakdown?.moonCondition ?? 20} pts</span>
                                        </div>
                                        <div className="bg-astro-card/40 p-2 rounded border border-astro-border/40">
                                          <span className="text-[10px] text-astro-secondary-text block">Nakshatra:</span>
                                          <span className="font-bold text-astro-primary">{item.muhurta.score.breakdown.nakshatra} / {item.muhurta.score.maxBreakdown?.nakshatra ?? 15} pts</span>
                                        </div>
                                        <div className="bg-astro-card/40 p-2 rounded border border-astro-border/40">
                                          <span className="text-[10px] text-astro-secondary-text block">Tithi:</span>
                                          <span className="font-bold text-astro-primary">{item.muhurta.score.breakdown.tithi} / {item.muhurta.score.maxBreakdown?.tithi ?? 15} pts</span>
                                        </div>
                                      </div>
                                    </div>

                                    {item.muhurta.score.cautions && item.muhurta.score.cautions.length > 0 && (
                                      <div className="pt-1">
                                        <span className="text-[10px] font-bold text-amber-900 block mb-1">
                                          {isHi ? 'सावधानियां / परहेज:' : 'Cautions to Observe:'}
                                        </span>
                                        <ul className="text-[11px] text-astro-secondary-text space-y-1">
                                          {item.muhurta.score.cautions.map((c, cIdx) => (
                                            <li key={cIdx} className="flex items-start gap-1">
                                              <AlertTriangle className="w-3 h-3 text-amber-600 mt-0.5 shrink-0" />
                                              <span>{c}</span>
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Action Buttons Footer for This Candidate */}
                                <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-amber-100">
                                  <button
                                    type="button"
                                    onClick={() => toggleFutureExpanded(futureId)}
                                    className="text-xs font-bold text-astro-primary hover:text-astro-accent transition-colors flex items-center gap-1 cursor-pointer"
                                  >
                                    {isExpanded ? (
                                      <>
                                        <ChevronUp className="w-3.5 h-3.5" />
                                        <span>{isHi ? 'कम विवरण देखें' : 'Hide Details'}</span>
                                      </>
                                    ) : (
                                      <>
                                        <ChevronDown className="w-3.5 h-3.5" />
                                        <span>{isHi ? 'विस्तृत पंचांग देखें' : 'View Full Details'}</span>
                                      </>
                                    )}
                                  </button>

                                  <div className="flex items-center gap-2">
                                    <a
                                      href={`https://api.whatsapp.com/send?text=${encodeURIComponent(
                                        `🌟 *Next 3 Months Golden Muhurta Alert*\n` +
                                        `📌 *Activity:* ${activeMilestone?.label || selectedMilestone}\n` +
                                        `📅 *Date:* ${itemDate.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}\n` +
                                        `⏰ *Time:* ${formatSmartDateTime(item.muhurta.startTime)} - ${formatSmartDateTime(item.muhurta.endTime)}\n` +
                                        `✨ *Score:* ${item.muhurta.score.totalScore}/100 (${item.muhurta.score.label})\n` +
                                        `⭐ *Nakshatra:* ${item.muhurta.panchang.nakshatra} (Pada ${item.muhurta.panchang.nakshatraPada})\n` +
                                        `🌙 *Tithi:* ${item.muhurta.panchang.tithi}\n` +
                                        `📍 *Location:* ${item.locationName}`
                                      )}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                                    >
                                      <ExternalLink className="w-3.5 h-3.5" />
                                      <span>{isHi ? 'व्हाट्सएप' : 'WhatsApp'}</span>
                                    </a>

                                    {isDatesModifiedFromOriginal && new Date(item.muhurta.dateTime).toISOString().split('T')[0] === startDate ? (
                                      <button
                                        type="button"
                                        onClick={handleRevertToOriginalDate}
                                        className="px-3.5 py-1.5 rounded-xl bg-amber-800 hover:bg-amber-900 text-white font-bold text-xs transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                                        title={isHi ? 'मूल अनुरोधित तारीख पर वापस जाएं' : 'Return to originally requested date'}
                                      >
                                        <RotateCcw className="w-3.5 h-3.5 text-amber-200" />
                                        <span>{isHi ? 'मूल तारीख पर वापस जाएं' : 'Back to Original Date'}</span>
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={() => handleAdoptFutureMuhurta(item.muhurta)}
                                        className="px-3.5 py-1.5 rounded-xl bg-astro-primary hover:bg-astro-primary/90 text-white font-bold text-xs transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                                      >
                                        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
                                        <span>{isHi ? 'इस मुहूर्त पर स्विच करें' : 'Switch to this Date'}</span>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Pagination / Show More Controls */}
                        {betterFutureMuhurtas.length > 6 && (
                          <div className="flex items-center justify-center pt-2">
                            {visibleFutureCount < betterFutureMuhurtas.length ? (
                              <button
                                type="button"
                                onClick={() => setVisibleFutureCount(prev => Math.min(prev + 6, betterFutureMuhurtas.length))}
                                className="px-5 py-2 rounded-xl bg-amber-100 hover:bg-amber-200 text-amber-950 font-bold text-xs border border-amber-300 transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                              >
                                <ChevronDown className="w-4 h-4" />
                                <span>
                                  {isHi
                                    ? `और ${betterFutureMuhurtas.length - visibleFutureCount} मुहूर्त दिखाएं (कुल ${betterFutureMuhurtas.length})`
                                    : `Show More Muhurtas (${betterFutureMuhurtas.length - visibleFutureCount} remaining)`}
                                </span>
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => setVisibleFutureCount(6)}
                                className="px-5 py-2 rounded-xl bg-white hover:bg-amber-50 text-astro-primary font-bold text-xs border border-amber-200 transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                              >
                                <ChevronUp className="w-4 h-4" />
                                <span>{isHi ? 'शीर्ष ६ मुहूर्त ही दिखाएं' : 'Show Top 6 Muhurtas'}</span>
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {!isCheckingFuture && betterFutureMuhurtas.length === 0 && Object.values(resultsByLocation).flat().length > 0 && (
                    <div className="text-center py-3 text-[11px] text-emerald-800 bg-emerald-50/70 rounded-xl border border-emerald-200/60 flex items-center justify-center gap-2">
                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      <span>{isHi ? 'आपकी चुनी गई तारीखों में पहले से ही अगले ३ माह का सर्वश्रेष्ठ मुहूर्त शामिल है।' : 'Your selected date range already contains the peak auspicious alignment in the next 3 months.'}</span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-astro-bg border border-astro-border border-dashed rounded-3xl py-16 sm:py-24 flex flex-col items-center justify-center text-center px-6 sm:px-12" id="no-muhurta-state">
                   <div className="w-16 h-16 bg-astro-card rounded-full flex items-center justify-center mb-4">
                      <Search className="w-8 h-8 text-astro-primary opacity-30" />
                   </div>
                   <h3 className="text-lg sm:text-xl font-bold text-astro-primary font-serif mb-2">
                     {isHi ? 'इस अवधि में कोई अनुकूल मुहूर्त नहीं मिला' : 'No Auspicious Windows in Selected Date Range'}
                   </h3>

                   {/* Explanatory Filter Notice */}
                   <div className="max-w-md bg-white border border-amber-200/80 rounded-2xl p-3.5 mb-6 text-left space-y-2 shadow-2xs">
                     <span className="text-[10px] font-bold uppercase tracking-wider text-amber-900 block">
                       {isHi ? 'सक्रिय वैदिक मापदंड (Active Filters):' : 'Strict Filters Active:'}
                     </span>
                     <div className="flex flex-wrap gap-1.5 text-xs">
                       {selectedTaraFilter !== 'all_auspicious' && (
                         <span className="px-2 py-0.5 rounded-md bg-amber-100/80 text-amber-950 font-semibold border border-amber-300">
                           ⭐ {selectedTaraFilter === 'kshema_only' ? (isHi ? 'केवल क्षेम तारा' : 'Kshema Only')
                               : selectedTaraFilter === 'sadhaka_only' ? (isHi ? 'केवल साधक तारा' : 'Sadhaka Only')
                               : selectedTaraFilter === 'sampat_only' ? (isHi ? 'केवल सम्पत् तारा' : 'Sampat Only')
                               : selectedTaraFilter === 'mitra_only' ? (isHi ? 'केवल मित्र तारा' : 'Mitra Only')
                               : (isHi ? 'कस्टम नवतारा' : 'Custom Navatara')}
                         </span>
                       )}
                       {shuddhiOptions.requireSthiraLagna && (
                         <span className="px-2 py-0.5 rounded-md bg-blue-100/80 text-blue-950 font-semibold border border-blue-300">
                           🏛️ {isHi ? 'अनिवार्य स्थिर लग्न' : 'Strict Sthira Lagna'}
                         </span>
                       )}
                       {shuddhiOptions.requireCharaLagna && (
                         <span className="px-2 py-0.5 rounded-md bg-purple-100/80 text-purple-950 font-semibold border border-purple-300">
                           ⚡ {isHi ? 'अनिवार्य चर लग्न' : 'Strict Chara Lagna'}
                         </span>
                       )}
                       {shuddhiOptions.ashtamaShuddhi && (
                         <span className="px-2 py-0.5 rounded-md bg-rose-100/80 text-rose-950 font-semibold border border-rose-300">
                           🛡️ {isHi ? 'अष्टम भाव शुद्धि' : '8H Ashtama Shuddhi'}
                         </span>
                       )}
                     </div>
                     <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                       {isHi 
                         ? 'आपकी चुनी गई ७ दिवसीय अवधि में इन कठोर नियमों को पूरा करने वाला कोई समय नहीं मिला।' 
                         : 'None of the transits in this 7-day window satisfied all strict conditions simultaneously.'}
                     </p>
                   </div>

                   {/* Quick Relaxation Shortcuts */}
                   <div className="flex flex-wrap items-center justify-center gap-2.5 mb-6">
                     {selectedTaraFilter !== 'all_auspicious' && (
                       <button
                         type="button"
                         onClick={() => {
                           setSelectedTaraFilter('all_auspicious');
                           try { localStorage.setItem(taraFilterStorageKey, 'all_auspicious'); } catch {}
                           handleSearch(primaryFocus, undefined, undefined, shuddhiOptions, 'all_auspicious');
                         }}
                         className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-amber-950 font-bold text-xs shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                       >
                         <Sparkles className="w-3.5 h-3.5 text-amber-950" />
                         <span>{isHi ? 'सभी शुभ तारे स्वीकार करें (Broaden Tara)' : 'Broaden to All Auspicious Taras'}</span>
                       </button>
                     )}

                     {(shuddhiOptions.requireSthiraLagna || shuddhiOptions.requireCharaLagna) && (
                       <button
                         type="button"
                         onClick={() => {
                           const relaxed = { ...shuddhiOptions, requireSthiraLagna: false, requireCharaLagna: false };
                           setShuddhiOptions(relaxed);
                           setIsUserCustomizedShuddhi(true);
                           try { localStorage.setItem(shuddhiStorageKey, JSON.stringify(relaxed)); } catch {}
                           handleSearch(primaryFocus, undefined, undefined, relaxed, selectedTaraFilter);
                         }}
                         className="px-4 py-2 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-900 border border-blue-300 font-bold text-xs shadow-2xs transition-all flex items-center gap-1.5 cursor-pointer"
                       >
                         <Building2 className="w-3.5 h-3.5 text-blue-700" />
                         <span>{isHi ? 'लग्न नियम शिथिल करें (Any Lagna)' : 'Allow Any Lagna Modality'}</span>
                       </button>
                     )}
                   </div>

                   {isCheckingFuture && (
                     <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 mb-6 flex items-center gap-2 text-xs text-amber-900 animate-pulse">
                       <Sparkles className="w-4 h-4 text-amber-600 animate-spin" />
                       <span>{isHi ? 'अगले ३ माह में श्रेष्ठ मुहूर्त खोजा जा रहा है...' : 'Checking if golden Muhurtas exist in the next 3 months...'}</span>
                     </div>
                   )}

                   {!isCheckingFuture && betterFutureMuhurtas.length > 0 && (
                     <div className="bg-amber-50/90 border border-amber-300 rounded-2xl p-4 mb-6 max-w-lg text-left space-y-3 shadow-xs">
                       <div className="flex items-center justify-between gap-2">
                         <div className="flex items-center gap-2">
                           <Sparkles className="w-4 h-4 text-amber-600 shrink-0" />
                           <span className="font-bold text-xs sm:text-sm text-astro-primary font-serif">
                             {isHi ? `अगले ३ माह में ${betterFutureMuhurtas.length} शुभ मुहूर्त उपलब्ध हैं!` : `${betterFutureMuhurtas.length} Golden Muhurtas Found in Next 3 Months!`}
                           </span>
                         </div>
                         <button
                           type="button"
                           onClick={() => handleAdoptFutureMuhurta(betterFutureMuhurtas[0].muhurta)}
                           className="px-3 py-1 rounded-lg bg-amber-900 hover:bg-amber-950 text-white text-xs font-bold shadow-2xs transition-all flex items-center gap-1 cursor-pointer"
                         >
                           <span>{isHi ? 'निकटतम मैच पर जाएं' : 'Jump to Next Match'}</span>
                         </button>
                       </div>
                       <p className="text-[11px] text-astro-secondary-text">
                         {isHi ? 'चुनी गई तारीखों में कोई अनुकूल समय नहीं था, परंतु आगामी ९० दिनों में ये श्रेष्ठ मुहूर्त उपलब्ध हैं:' : 'Your requested window had no auspicious slots, but these superior dates are available within the next 90 days:'}
                       </p>
                       <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                         {[...betterFutureMuhurtas].sort((a, b) => compareFutureMuhurtas(a, b, sortOrder)).slice(0, 5).map((bm, bIdx) => (
                           <div key={bIdx} className="bg-white p-2.5 rounded-xl border border-amber-200 flex items-center justify-between gap-2 text-xs">
                             <div>
                               <span className="font-bold text-astro-primary block">
                                 {new Date(bm.muhurta.dateTime).toLocaleDateString(isHi ? 'hi-IN' : 'en-US', {
                                   weekday: 'short',
                                   day: 'numeric',
                                   month: 'short',
                                   year: 'numeric'
                                 })}
                               </span>
                               <span className="text-[10px] text-astro-secondary-text">
                                 {formatSmartDateTime(bm.muhurta.startTime)} - {formatSmartDateTime(bm.muhurta.endTime)} • {bm.muhurta.panchang.nakshatra}
                               </span>
                             </div>
                             <div className="flex items-center gap-2 shrink-0">
                               <span className="text-xs font-bold text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">
                                 {bm.muhurta.score.totalScore}%
                               </span>
                               <button
                                 type="button"
                                 onClick={() => handleAdoptFutureMuhurta(bm.muhurta)}
                                 className="px-2.5 py-1 rounded-lg bg-astro-primary text-white text-[10px] font-bold hover:bg-astro-primary/90 cursor-pointer"
                               >
                                 {isHi ? 'स्विच करें' : 'Switch'}
                               </button>
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}

                   <div className="flex flex-wrap gap-3">
                     {isDatesModifiedFromOriginal && originalSearchDates && (
                       <button
                         type="button"
                         onClick={handleRevertToOriginalDate}
                         className="bg-amber-800 hover:bg-amber-900 text-white px-6 py-2.5 rounded-xl font-bold text-xs shadow-md flex items-center gap-1.5 cursor-pointer transition-all"
                       >
                         <RotateCcw className="w-3.5 h-3.5 text-amber-200" />
                         <span>{isHi ? 'मूल अनुरोधित तारीख पर वापस जाएं' : 'Back to Original Requested Date'}</span>
                       </button>
                     )}
                     <button 
                       onClick={() => setCurrentStep('gathering')}
                       className="bg-astro-primary text-white px-6 py-2.5 rounded-xl font-bold text-xs shadow-md cursor-pointer"
                     >
                        {isHi ? 'खोज बदलें' : 'Modify Search Criteria'}
                     </button>
                     <button 
                       onClick={restartSearch}
                       className="bg-white text-astro-primary border border-astro-primary px-6 py-2.5 rounded-xl font-bold text-xs shadow-md cursor-pointer"
                     >
                        {isHi ? 'पुनः प्रारंभ करें' : 'Restart Advisor'}
                     </button>
                   </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* SCORE EXPLANATION MODAL (Answers why Combo differs from Navtara/Rashi) */}
      {showScoreExplanationModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200"
          onClick={() => setShowScoreExplanationModal(false)}
        >
          <div 
            className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl border border-astro-border p-6 sm:p-8 space-y-6 relative text-left"
            onClick={e => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setShowScoreExplanationModal(false)}
              className="absolute top-5 right-5 p-2 rounded-full text-astro-secondary-text hover:text-astro-primary hover:bg-astro-bg transition-colors cursor-pointer"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Header */}
            <div className="flex items-start gap-3.5 pr-8">
              <div className="w-12 h-12 rounded-2xl bg-amber-500/15 text-amber-800 flex items-center justify-center font-bold text-2xl shrink-0 border border-amber-300">
                🧮
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-bold font-serif text-astro-primary leading-tight">
                  {isHi ? 'संयुक्त (Combo), नवतारा एवं राशि अंक की गणना विधि' : 'Understanding Combined (Combo) vs Navtara vs Rashi Scores'}
                </h3>
                <p className="text-xs text-astro-secondary-text mt-1">
                  {isHi 
                    ? 'सर्वदोष निवारण सिद्धांत एवं विभिन्न दृष्टिकोणों में अंकों का वैदिक गणित' 
                    : 'The Vedic astrological mathematics of Sarva-Dosha Nivarana & Framework Normalization'}
                </p>
              </div>
            </div>

            {/* Specific Answer to User's Question */}
            <div className="bg-amber-50 border border-amber-300/80 rounded-2xl p-4 sm:p-5 text-xs space-y-2.5 text-amber-950">
              <div className="flex items-center gap-2 font-bold text-sm text-amber-900">
                <HelpCircle className="w-4 h-4 text-amber-700 shrink-0" />
                <span>{isHi ? 'प्रश्न: नवतारा ९६% और राशि ९४% होने पर भी संयुक्त (Combo) ९२% क्यों है?' : 'Why is Combined 92% when Navtara is 96% and Rashi is 94%?'}</span>
              </div>
              <p className="leading-relaxed text-[11px] text-amber-900/90">
                {isHi
                  ? 'संयुक्त (Combo) अंक कोई सीधा औसत ((९६ + ९४) ÷ २ = ९५%) नहीं है! वैदिक मुहूर्त शास्त्र में "सर्वदोष निवारण" का नियम लागू होता है। नवतारा केवल जन्म नक्षत्र और तारा बल को ६५% प्राथमिकता देता है। राशि केवल चंद्र गोचर और तिथि को ५५% प्राथमिकता देती है। परंतु संयुक्त (Combo) मोड में सातों शास्त्रीय अंगों (तारा बल, चंद्र गोचर, नक्षत्र, तिथि, वार, राहुकाल एवं शुभ होरा) का एक साथ परीक्षण होता है।'
                  : 'Combined score is NOT a simple arithmetic average ((96% + 94%) / 2 = 95%)! Vedic Muhurta Shastra operates on the classical principle of Sarva-Dosha Nivarana (comprehensive defect nullification). Navtara mode evaluates primarily your Janma Nakshatra and Tara Bala (65% weight). Rashi mode evaluates Moon\'s house transit from your natal Moon and Tithi (55% weight). Combined (Combo) mode evaluates all 7 limbs simultaneously.'}
              </p>
              <div className="p-3 bg-white/80 rounded-xl border border-amber-200/80 text-[11px] space-y-1">
                <span className="font-bold text-amber-950 block">
                  {isHi ? 'वास्तविक अंक संरचना का उदाहरण (Score Anatomy):' : 'Real-World Scoring Breakdown:'}
                </span>
                <p className="text-astro-secondary-text leading-relaxed">
                  {isHi
                    ? 'यदि तारा बल पूर्ण है (२५/२५ अंक) और चंद्र गोचर भी अनुकूल है (२०/२० अंक), परंतु उस समय की होरा मध्यम है (२/६ अंक) अथवा तिथि साधारण है (१०/१५ अंक), तो कुल संचित अंक ८८-८९/९७ होते हैं, जो सामान्यीकृत होकर ९२% बनते हैं। ९२% अंक "अति उत्तम (Excellent)" श्रेणी का एक अत्यंत शुभ मुहूर्त है!'
                    : 'Even if your Tara Bala is 100% full (25/25 pts) and Moon Transit is ideal (20/20 pts), if secondary limbs like the planetary Hora are neutral (2/6 pts) or Tithi is standard (10/15 pts), the normalized score is (89 / 97) × 100 = 92%. A 92% composite score is in the "Excellent" tier and represents pristine harmony across all 7 Vedic limbs!'}
                </p>
              </div>
            </div>

            {/* Framework Comparison Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Combo */}
              <div className="bg-astro-card/60 border border-astro-border rounded-2xl p-4 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                  <span>Combo (Default)</span>
                </div>
                <span className="text-[10px] text-astro-secondary-text block">
                  {isHi ? 'सर्वदोष निवारण (७ अंग)' : '7 Classical Vedic Limbs'}
                </span>
                <ul className="text-[11px] space-y-1 text-astro-text">
                  <li>• Tara Bala: 25 pts</li>
                  <li>• Chandra Gochar: 20 pts</li>
                  <li>• Nakshatra: 15 pts</li>
                  <li>• Tithi: 15 pts</li>
                  <li>• Vara (Weekday): 8 pts</li>
                  <li>• Rahu / Yamaganda: 8 pts</li>
                  <li>• Benefic Hora: 6 pts</li>
                </ul>
              </div>

              {/* Navtara */}
              <div className="bg-astro-card/60 border border-astro-border rounded-2xl p-4 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary">
                  <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                  <span>Navtara Focus</span>
                </div>
                <span className="text-[10px] text-astro-secondary-text block">
                  {isHi ? 'जन्म नक्षत्र एवं तारा बल प्रधान' : 'Star & Tara Dominance'}
                </span>
                <ul className="text-[11px] space-y-1 text-astro-text">
                  <li>• Tara Bala: 40 pts (Dominant)</li>
                  <li>• Nakshatra: 25 pts</li>
                  <li>• Tithi: 10 pts</li>
                  <li>• Chandra Gochar: 8 pts</li>
                  <li>• Vara & Rahu: 14 pts</li>
                  <li>• Hora: 3 pts</li>
                </ul>
              </div>

              {/* Rashi */}
              <div className="bg-astro-card/60 border border-astro-border rounded-2xl p-4 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-bold text-astro-primary">
                  <Compass className="w-3.5 h-3.5 text-blue-500" />
                  <span>Rashi Focus</span>
                </div>
                <span className="text-[10px] text-astro-secondary-text block">
                  {isHi ? 'दृक् पंचांग एवं चंद्र गोचर प्रधान' : 'Moon Transit & Tithi'}
                </span>
                <ul className="text-[11px] space-y-1 text-astro-text">
                  <li>• Chandra Gochar: 35 pts (Dominant)</li>
                  <li>• Tithi: 20 pts</li>
                  <li>• Vara (Weekday): 15 pts</li>
                  <li>• Tara Bala: 10 pts</li>
                  <li>• Rahu / Yamaganda: 8 pts</li>
                  <li>• Nakshatra: 6 pts</li>
                </ul>
              </div>
            </div>

            {/* Vedic Shuddhi & Lagna Modality Note */}
            <div className="p-3.5 bg-emerald-50/60 border border-emerald-200 rounded-2xl flex items-start gap-3 text-xs">
              <Building2 className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <span className="font-bold text-emerald-950 block">
                  {isHi ? 'वैदिक शुद्धि, स्थिर/चर लग्न एवं सिद्धि योग' : 'Vedic Shuddhi, Sthira/Chara Lagna & Auspicious Yogas'}
                </span>
                <p className="text-[11px] text-emerald-900 leading-relaxed">
                  {isHi
                    ? 'जब आप स्थिर लग्न (Sthira Lagna) अथवा अष्टम शुद्धि विकल्प चुनते हैं, तो इंजन लग्न शुद्धि के अनुसार अंकों में विशेष शोधन जोड़ता है। साथ ही गुरु-पुष्य, रवि-पुष्य व सर्वार्थ सिद्धि जैसे विशेष योग मिलने पर +5 से +15 अंकों का अतिरिक्त शास्त्रीय बोनस मिलता है!'
                    : 'When Vedic Shuddhi rules (Sthira Lagna, 8H Ashtama Shuddhi, Upachaya placement) are enabled, the engine evaluates the rising sign of the window and incorporates up to +25 Shuddhi points. Rare Siddhi Yogas (Guru-Pushya, Ravi-Pushya, Sarvartha Siddhi) grant up to +15 bonus points!'}
                </p>
              </div>
            </div>

            {/* Individual Pillar Scores Explanation */}
            <div className="p-4 bg-astro-card/60 border border-astro-border rounded-2xl space-y-2.5 text-xs">
              <div className="flex items-center gap-2 font-bold text-astro-primary">
                <Sparkles className="w-4 h-4 text-astro-accent" />
                <span>{isHi ? 'व्यक्तिगत अंग अंक विभाजन (Individual Pillar Scores Explained)' : 'Individual Pillar Scores & Vedic Mathematics'}</span>
              </div>
              <p className="text-[11px] text-astro-secondary-text leading-relaxed">
                {isHi
                  ? 'मुहूर्त कार्ड में अब आपको केवल कुल प्रतिशत ही नहीं, बल्कि चारों प्रमुख वैदिक स्तंभों के व्यक्तिगत अंक स्पष्ट दिखाई देते हैं:'
                  : 'Rather than a black-box percentage, each Muhurta window provides transparent sub-scores for all four classical pillars:'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                <div className="bg-white p-2.5 rounded-xl border border-astro-border/60 space-y-0.5">
                  <span className="font-bold text-emerald-800 text-[11px] block">🌟 Tara Bala (25 pts max)</span>
                  <span className="text-[10px] text-astro-text block leading-normal">
                    {isHi ? 'साधक के जन्म नक्षत्र से गोचर नक्षत्र का संबंध (संपत, साधक, मित्र, परम मित्र = १००%)।' : 'Navatara resonance from natal moon star (Sampat, Sadhaka, Mitra, Param Mitra = 100%).'}
                  </span>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-astro-border/60 space-y-0.5">
                  <span className="font-bold text-emerald-800 text-[11px] block">🏛️ Vedic Lagna & Shuddhi (25 pts max)</span>
                  <span className="text-[10px] text-astro-text block leading-normal">
                    {isHi ? 'लग्न स्थिरता (१० अंक), अष्टम शुद्धि (८ अंक), उपचय/केंद्र ग्रह स्थिति (१२ अंक)।' : 'Ascendant modality (10 pts), Ashtama Shuddhi 8H (8 pts), Upachaya/Kendra placements (12 pts).'}
                  </span>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-astro-border/60 space-y-0.5">
                  <span className="font-bold text-blue-800 text-[11px] block">🌙 Chandra Bala (20 pts max)</span>
                  <span className="text-[10px] text-astro-text block leading-normal">
                    {isHi ? 'जन्म राशि से चंद्र की गोचर स्थिति (३, ६, ७, १०, ११ भाव = शुभ)।' : 'Lunar transit from natal moon sign (3rd, 6th, 7th, 10th, 11th houses = highly auspicious).'}
                  </span>
                </div>
                <div className="bg-white p-2.5 rounded-xl border border-astro-border/60 space-y-0.5">
                  <span className="font-bold text-amber-800 text-[11px] block">☀️ Panchanga Limbs (40 pts max)</span>
                  <span className="text-[10px] text-astro-text block leading-normal">
                    {isHi ? 'तिथि (१५), नक्षत्र (१५), वार (८) एवं शुभ होरा (६ अंक)।' : 'Harmonious Tithi (15), auspicious Nakshatra (15), favorable Vara (8), and benefic Hora (6).'}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer Close Button */}
            <div className="flex justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowScoreExplanationModal(false)}
                className="px-6 py-2.5 rounded-xl bg-astro-primary text-white text-xs font-bold hover:bg-astro-primary/90 transition-all cursor-pointer shadow-xs"
              >
                {isHi ? 'समझ गया (बंद करें)' : 'Understood, Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Non-disruptive Toast Notification (Requirement 14) */}
      {notification && (
        <div 
          role="alert"
          className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-xs sm:text-sm font-medium animate-fadeIn transition-all max-w-md ${
            notification.type === 'error' ? 'bg-rose-900 border-rose-700 text-rose-50' :
            notification.type === 'warning' ? 'bg-amber-900 border-amber-700 text-amber-50' :
            'bg-indigo-900 border-indigo-700 text-indigo-50'
          }`}
        >
          <span>{notification.message}</span>
          <button 
            type="button"
            onClick={() => setNotification(null)} 
            className="text-white/80 hover:text-white ml-2 text-xs font-bold cursor-pointer"
            aria-label="Dismiss notification"
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};
